// src/index.ts
export * from "@grimoire/shared";

// src/entities/work.ts
import { newId } from "@grimoire/shared";
var ContentType = /* @__PURE__ */ ((ContentType2) => {
  ContentType2["Manga"] = "manga";
  ContentType2["Manhwa"] = "manhwa";
  ContentType2["Manhua"] = "manhua";
  ContentType2["Webtoon"] = "webtoon";
  ContentType2["LightNovel"] = "light_novel";
  ContentType2["WebNovel"] = "web_novel";
  ContentType2["WesternNovel"] = "western_novel";
  ContentType2["Anime"] = "anime";
  ContentType2["Donghua"] = "donghua";
  ContentType2["EPUB"] = "epub";
  ContentType2["PDF"] = "pdf";
  ContentType2["CBZ"] = "cbz";
  ContentType2["Audiobook"] = "audiobook";
  ContentType2["Fanfiction"] = "fanfiction";
  ContentType2["Movie"] = "movie";
  ContentType2["CDrama"] = "cdrama";
  ContentType2["KDrama"] = "kdrama";
  ContentType2["JDrama"] = "jdrama";
  ContentType2["Series"] = "series";
  return ContentType2;
})(ContentType || {});
var ContentOrigin = /* @__PURE__ */ ((ContentOrigin2) => {
  ContentOrigin2["Korean"] = "korean";
  ContentOrigin2["Japanese"] = "japanese";
  ContentOrigin2["Chinese"] = "chinese";
  ContentOrigin2["Western"] = "western";
  ContentOrigin2["Original"] = "original";
  ContentOrigin2["Unknown"] = "unknown";
  return ContentOrigin2;
})(ContentOrigin || {});
var PublicationState = /* @__PURE__ */ ((PublicationState2) => {
  PublicationState2["Ongoing"] = "ongoing";
  PublicationState2["Completed"] = "completed";
  PublicationState2["Hiatus"] = "hiatus";
  PublicationState2["Cancelled"] = "cancelled";
  PublicationState2["Unknown"] = "unknown";
  return PublicationState2;
})(PublicationState || {});
var PrivacyState = /* @__PURE__ */ ((PrivacyState2) => {
  PrivacyState2["Public"] = "public";
  PrivacyState2["Private"] = "private";
  PrivacyState2["Vault"] = "vault";
  return PrivacyState2;
})(PrivacyState || {});
function createWork(params) {
  validateWorkInvariant(params);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    id: newId(),
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    mergedIntoWorkId: params.mergedIntoWorkId ?? null
  };
}
function applyWorkPatch(work, patch) {
  const candidate = { ...work, ...patch, updatedAt: (/* @__PURE__ */ new Date()).toISOString() };
  validateWorkInvariant(candidate);
  return candidate;
}
function toWorkSummary(work) {
  return {
    id: work.id,
    title: work.title,
    aliases: work.aliases,
    contentType: work.contentType,
    origin: work.origin,
    publicationState: work.publicationState,
    privacyState: work.privacyState,
    coverRef: work.coverRef,
    genres: work.genres,
    updatedAt: work.updatedAt,
    deletedAt: work.deletedAt,
    mergedIntoWorkId: work.mergedIntoWorkId ?? null
  };
}
function validateWorkInvariant(candidate) {
  if (!candidate.title.trim()) {
    throw new Error("Work invariant violated: title must not be blank");
  }
}

// src/entities/reading-relationship.ts
import {
  newId as newId3
} from "@grimoire/shared";

// src/entities/progress.ts
import {
  newId as newId2
} from "@grimoire/shared";
var ProgressMethod = /* @__PURE__ */ ((ProgressMethod2) => {
  ProgressMethod2["Manual"] = "manual";
  ProgressMethod2["Reader"] = "reader";
  ProgressMethod2["BrowserAssisted"] = "browser_assisted";
  ProgressMethod2["Provider"] = "provider";
  ProgressMethod2["Feed"] = "feed";
  ProgressMethod2["Import"] = "import";
  ProgressMethod2["ExternalTracker"] = "external_tracker";
  return ProgressMethod2;
})(ProgressMethod || {});
var ProgressConfidence = /* @__PURE__ */ ((ProgressConfidence2) => {
  ProgressConfidence2["Confirmed"] = "confirmed";
  ProgressConfidence2["Inferred"] = "inferred";
  ProgressConfidence2["Scheduled"] = "scheduled";
  ProgressConfidence2["Unknown"] = "unknown";
  ProgressConfidence2["Stale"] = "stale";
  return ProgressConfidence2;
})(ProgressConfidence || {});
function formatProgressAnchor(anchor) {
  switch (anchor.type) {
    case "chapter": {
      const sub = anchor.subchapter !== void 0 ? `.${anchor.subchapter}` : "";
      const title = anchor.title ? ` - ${anchor.title}` : "";
      return `Ch. ${anchor.chapter}${sub}${title}`;
    }
    case "episode": {
      const tc = anchor.timecode ? ` (${anchor.timecode})` : "";
      return `Ep. ${anchor.episode}${tc}`;
    }
    case "page": {
      const total = anchor.totalPages !== void 0 ? `/${anchor.totalPages}` : "";
      return `p. ${anchor.page}${total}`;
    }
    case "percent": {
      return `${Math.round(anchor.percent)}%`;
    }
    case "epub_locator": {
      if (anchor.chapterTitle) {
        return anchor.percent !== void 0 ? `${anchor.chapterTitle} (${Math.round(anchor.percent)}%)` : anchor.chapterTitle;
      }
      return anchor.percent !== void 0 ? `EPUB ${Math.round(anchor.percent)}%` : "EPUB Bookmark";
    }
    case "volume_chapter": {
      const title = anchor.title ? ` - ${anchor.title}` : "";
      return `Vol. ${anchor.volume} Ch. ${anchor.chapter}${title}`;
    }
    case "provider": {
      return anchor.display;
    }
    case "audiobook": {
      const tc = anchor.timecode ? ` (${anchor.timecode})` : "";
      const narr = anchor.narrator ? ` \u2014 Narrated by ${anchor.narrator}` : "";
      return `Track ${anchor.track}${tc}${narr}`;
    }
  }
}
var displayProgress = formatProgressAnchor;
function extractNumericProgress(anchor) {
  switch (anchor.type) {
    case "chapter":
      return anchor.chapter + (anchor.subchapter ? anchor.subchapter * 0.1 : 0);
    case "episode":
      return anchor.episode;
    case "page":
      return anchor.page;
    case "percent":
      return anchor.percent;
    case "volume_chapter":
      return anchor.volume * 1e3 + anchor.chapter;
    case "epub_locator":
      return anchor.percent ?? null;
    case "provider":
      return null;
    case "audiobook":
      return anchor.track;
  }
}
function normalizeProgressAnchor(raw) {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw;
  const discriminant = typeof obj["type"] === "string" ? obj["type"] : typeof obj["kind"] === "string" ? obj["kind"] : void 0;
  if (discriminant === "chapter") {
    const chapter = Number(obj["chapter"] ?? obj["chapterNumber"] ?? 0);
    const subchapterRaw = obj["subchapter"] ?? obj["subChapter"];
    const subchapter = typeof subchapterRaw === "number" && Number.isFinite(subchapterRaw) ? subchapterRaw : void 0;
    const title = typeof obj["title"] === "string" ? obj["title"] : void 0;
    return {
      type: "chapter",
      chapter: Number.isFinite(chapter) && chapter >= 0 ? chapter : 0,
      ...subchapter !== void 0 ? { subchapter } : {},
      ...title ? { title } : {}
    };
  }
  if (discriminant === "episode") {
    const episode = Number(obj["episode"] ?? obj["episodeNumber"] ?? 0);
    const seasonRaw = obj["season"] ?? obj["seasonNumber"];
    const season = typeof seasonRaw === "number" && Number.isFinite(seasonRaw) ? seasonRaw : void 0;
    const timecode = typeof obj["timecode"] === "string" ? obj["timecode"] : void 0;
    const title = typeof obj["title"] === "string" ? obj["title"] : void 0;
    return {
      type: "episode",
      episode: Number.isFinite(episode) && episode >= 0 ? episode : 0,
      ...season !== void 0 ? { season } : {},
      ...timecode ? { timecode } : {},
      ...title ? { title } : {}
    };
  }
  if (discriminant === "audiobook" || discriminant === "audio") {
    const track = Number(obj["track"] ?? obj["chapter"] ?? obj["trackNumber"] ?? 1);
    const timecode = typeof obj["timecode"] === "string" ? obj["timecode"] : void 0;
    const timestampSeconds = typeof obj["timestampSeconds"] === "number" && Number.isFinite(obj["timestampSeconds"]) ? obj["timestampSeconds"] : void 0;
    const totalSeconds = typeof obj["totalSeconds"] === "number" && Number.isFinite(obj["totalSeconds"]) ? obj["totalSeconds"] : void 0;
    const narrator = typeof obj["narrator"] === "string" ? obj["narrator"] : void 0;
    const title = typeof obj["title"] === "string" ? obj["title"] : void 0;
    return {
      type: "audiobook",
      track: Number.isFinite(track) && track >= 0 ? track : 1,
      ...timecode ? { timecode } : {},
      ...timestampSeconds !== void 0 ? { timestampSeconds } : {},
      ...totalSeconds !== void 0 ? { totalSeconds } : {},
      ...narrator ? { narrator } : {},
      ...title ? { title } : {}
    };
  }
  if (discriminant === "page" || discriminant === "cbz_page" || discriminant === "pdf_page") {
    const page = Number(obj["page"] ?? obj["pageNumber"] ?? 0);
    const totalPagesRaw = obj["totalPages"];
    const totalPages = typeof totalPagesRaw === "number" && Number.isFinite(totalPagesRaw) ? totalPagesRaw : void 0;
    return {
      type: "page",
      page: Number.isFinite(page) && page >= 0 ? page : 0,
      ...totalPages !== void 0 ? { totalPages } : {}
    };
  }
  if (discriminant === "percent" || discriminant === "percentage") {
    const pct = Number(obj["percent"] ?? obj["percentage"] ?? 0);
    return {
      type: "percent",
      percent: Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0
    };
  }
  if (discriminant === "epub_locator" || discriminant === "epub_cfi") {
    const cfi = typeof obj["cfi"] === "string" && obj["cfi"].trim() ? obj["cfi"] : "epubcfi(/6/2!)";
    const pctRaw = obj["percent"] ?? obj["percentage"];
    const percent = typeof pctRaw === "number" && Number.isFinite(pctRaw) ? pctRaw : void 0;
    const chapterTitle = typeof obj["chapterTitle"] === "string" ? obj["chapterTitle"] : void 0;
    return {
      type: "epub_locator",
      cfi,
      ...percent !== void 0 ? { percent } : {},
      ...chapterTitle ? { chapterTitle } : {}
    };
  }
  if (discriminant === "volume_chapter") {
    const volume = Number(obj["volume"] ?? 1);
    const chapter = Number(obj["chapter"] ?? obj["chapterNumber"] ?? 1);
    const title = typeof obj["title"] === "string" ? obj["title"] : void 0;
    return {
      type: "volume_chapter",
      volume: Number.isFinite(volume) && volume >= 0 ? volume : 1,
      chapter: Number.isFinite(chapter) && chapter >= 0 ? chapter : 1,
      ...title ? { title } : {}
    };
  }
  if (discriminant === "provider") {
    const locator = typeof obj["locator"] === "string" && obj["locator"].trim() ? obj["locator"] : "external";
    const display = typeof obj["display"] === "string" && obj["display"].trim() ? obj["display"] : typeof obj["label"] === "string" && obj["label"].trim() ? obj["label"] : locator;
    return {
      type: "provider",
      locator,
      display
    };
  }
  if (typeof obj["chapter"] === "number" || typeof obj["chapterNumber"] === "number") {
    return {
      type: "chapter",
      chapter: Number(obj["chapter"] ?? obj["chapterNumber"] ?? 0)
    };
  }
  if (typeof obj["episode"] === "number" || typeof obj["episodeNumber"] === "number") {
    return {
      type: "episode",
      episode: Number(obj["episode"] ?? obj["episodeNumber"] ?? 0)
    };
  }
  if (typeof obj["page"] === "number" || typeof obj["pageNumber"] === "number") {
    return {
      type: "page",
      page: Number(obj["page"] ?? obj["pageNumber"] ?? 0)
    };
  }
  if (typeof obj["percent"] === "number" || typeof obj["percentage"] === "number") {
    return {
      type: "percent",
      percent: Number(obj["percent"] ?? obj["percentage"] ?? 0)
    };
  }
  return null;
}
function validateProgressAnchor(anchor) {
  switch (anchor.type) {
    case "chapter":
      if (anchor.chapter < 0 || !Number.isFinite(anchor.chapter)) {
        throw new Error(`ProgressAnchor chapter must be a non-negative finite number, got ${anchor.chapter}`);
      }
      if (anchor.subchapter !== void 0 && (anchor.subchapter < 0 || !Number.isFinite(anchor.subchapter))) {
        throw new Error(`ProgressAnchor subchapter must be non-negative, got ${anchor.subchapter}`);
      }
      break;
    case "episode":
      if (anchor.episode < 0 || !Number.isFinite(anchor.episode)) {
        throw new Error(`ProgressAnchor episode must be a non-negative finite number, got ${anchor.episode}`);
      }
      break;
    case "page":
      if (anchor.page < 0 || !Number.isFinite(anchor.page)) {
        throw new Error(`ProgressAnchor page must be non-negative, got ${anchor.page}`);
      }
      if (anchor.totalPages !== void 0 && anchor.totalPages < anchor.page) {
        throw new Error(
          `ProgressAnchor page (${anchor.page}) cannot exceed totalPages (${anchor.totalPages})`
        );
      }
      break;
    case "percent":
      if (anchor.percent < 0 || anchor.percent > 100 || !Number.isFinite(anchor.percent)) {
        throw new Error(`ProgressAnchor percent must be between 0 and 100, got ${anchor.percent}`);
      }
      break;
    case "epub_locator":
      if (!anchor.cfi || !anchor.cfi.trim()) {
        throw new Error("ProgressAnchor epub_locator requires a non-empty cfi");
      }
      if (anchor.percent !== void 0 && (anchor.percent < 0 || anchor.percent > 100)) {
        throw new Error(`ProgressAnchor epub_locator percent must be 0-100, got ${anchor.percent}`);
      }
      break;
    case "volume_chapter":
      if (anchor.volume < 0 || anchor.chapter < 0) {
        throw new Error(
          `ProgressAnchor volume_chapter volume and chapter must be non-negative, got Vol ${anchor.volume}, Ch ${anchor.chapter}`
        );
      }
      break;
    case "provider":
      if (!anchor.locator || !anchor.locator.trim()) {
        throw new Error("ProgressAnchor provider requires a non-empty locator");
      }
      if (!anchor.display || !anchor.display.trim()) {
        throw new Error("ProgressAnchor provider requires a non-empty display string");
      }
      break;
    case "audiobook":
      if (anchor.track < 0 || !Number.isFinite(anchor.track)) {
        throw new Error(`ProgressAnchor audiobook track must be a non-negative finite number, got ${anchor.track}`);
      }
      break;
  }
}
function createProgressEvent(params) {
  validateProgressAnchor(params.anchorAfter);
  if (params.anchorBefore) {
    validateProgressAnchor(params.anchorBefore);
  }
  return {
    ...params,
    id: params.id ?? newId2(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/reading-relationship.ts
var PersonalState = /* @__PURE__ */ ((PersonalState2) => {
  PersonalState2["Planned"] = "planned";
  PersonalState2["Reading"] = "reading";
  PersonalState2["Watching"] = "watching";
  PersonalState2["Paused"] = "paused";
  PersonalState2["Waiting"] = "waiting";
  PersonalState2["Stalled"] = "stalled";
  PersonalState2["Rereading"] = "rereading";
  PersonalState2["CaughtUp"] = "caught_up";
  PersonalState2["Completed"] = "completed";
  PersonalState2["Dropped"] = "dropped";
  PersonalState2["Archived"] = "archived";
  return PersonalState2;
})(PersonalState || {});
var ReadingPriority = /* @__PURE__ */ ((ReadingPriority2) => {
  ReadingPriority2["Low"] = "low";
  ReadingPriority2["Normal"] = "normal";
  ReadingPriority2["High"] = "high";
  ReadingPriority2["Urgent"] = "urgent";
  return ReadingPriority2;
})(ReadingPriority || {});
function validateReadingRelationshipInvariant(candidate) {
  if (candidate.currentAnchor) {
    validateProgressAnchor(candidate.currentAnchor);
  }
  if (candidate.personalState === "completed" /* Completed */ && !candidate.completedAt) {
  }
  if (candidate.personalState === "dropped" /* Dropped */ && candidate.stoppedAt === null && candidate.completedAt !== null) {
    throw new Error("ReadingRelationship invariant violated: dropped work cannot have completedAt without stoppedAt");
  }
}
function createReadingRelationship(params) {
  validateReadingRelationshipInvariant(params);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    id: params.id ?? newId3(),
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now
  };
}
function applyReadingRelationshipPatch(rel, patch) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const nextState = patch.personalState ?? rel.personalState;
  let completedAt = patch.completedAt !== void 0 ? patch.completedAt : rel.completedAt;
  if (patch.personalState === "completed" /* Completed */ && !completedAt) {
    completedAt = now;
  }
  let stoppedAt = patch.stoppedAt !== void 0 ? patch.stoppedAt : rel.stoppedAt;
  if ((patch.personalState === "dropped" /* Dropped */ || patch.personalState === "paused" /* Paused */) && !stoppedAt) {
    stoppedAt = now;
  }
  const updated = {
    ...rel,
    ...patch,
    personalState: nextState,
    completedAt,
    stoppedAt,
    updatedAt: now
  };
  validateReadingRelationshipInvariant(updated);
  return updated;
}
function createReadingAttempt(params) {
  if (params.finalAnchor) {
    validateProgressAnchor(params.finalAnchor);
  }
  if (params.attemptNumber < 1) {
    throw new Error(`ReadingAttempt attemptNumber must be >= 1, got ${params.attemptNumber}`);
  }
  return {
    ...params,
    id: params.id ?? newId3(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}
function createReadingSession(params) {
  if (params.anchorBefore) {
    validateProgressAnchor(params.anchorBefore);
  }
  if (params.anchorAfter) {
    validateProgressAnchor(params.anchorAfter);
  }
  if (params.durationSeconds !== null && params.durationSeconds !== void 0 && params.durationSeconds < 0) {
    throw new Error(`ReadingSession durationSeconds must be non-negative, got ${params.durationSeconds}`);
  }
  return {
    ...params,
    id: params.id ?? newId3(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/access-path.ts
import { newId as newId4 } from "@grimoire/shared";
var AccessPathKind = /* @__PURE__ */ ((AccessPathKind2) => {
  AccessPathKind2["Web"] = "web";
  AccessPathKind2["LocalFile"] = "local_file";
  AccessPathKind2["Feed"] = "feed";
  AccessPathKind2["ManualReference"] = "manual";
  AccessPathKind2["Manual"] = "manual";
  return AccessPathKind2;
})(AccessPathKind || {});
var AccessCapability = /* @__PURE__ */ ((AccessCapability2) => {
  AccessCapability2["Read"] = "read";
  AccessCapability2["Watch"] = "watch";
  AccessCapability2["Metadata"] = "metadata";
  AccessCapability2["ChapterListing"] = "chapter_listing";
  AccessCapability2["Updates"] = "updates";
  AccessCapability2["DirectOpening"] = "direct_opening";
  AccessCapability2["Tracking"] = "tracking";
  AccessCapability2["LocalFile"] = "local_file";
  AccessCapability2["Feed"] = "feed";
  return AccessCapability2;
})(AccessCapability || {});
var SourceHealth = /* @__PURE__ */ ((SourceHealth2) => {
  SourceHealth2["Healthy"] = "healthy";
  SourceHealth2["Degraded"] = "degraded";
  SourceHealth2["Unavailable"] = "unavailable";
  SourceHealth2["Stale"] = "stale";
  SourceHealth2["Unknown"] = "unknown";
  SourceHealth2["Local"] = "local";
  SourceHealth2["Broken"] = "broken";
  return SourceHealth2;
})(SourceHealth || {});
var SourceHealthState = SourceHealth;
function normalizeAccessPathHealth(path) {
  if (!path) return "unknown" /* Unknown */;
  return path.health ?? path.healthState ?? "unknown" /* Unknown */;
}
var SourceCapabilityStatus = /* @__PURE__ */ ((SourceCapabilityStatus2) => {
  SourceCapabilityStatus2["Supported"] = "supported";
  SourceCapabilityStatus2["Unsupported"] = "unsupported";
  SourceCapabilityStatus2["Unknown"] = "unknown";
  return SourceCapabilityStatus2;
})(SourceCapabilityStatus || {});
function toAccessPathSummary(path) {
  return {
    id: path.id,
    workId: path.workId,
    kind: path.kind,
    label: path.label,
    isPreferred: path.isPreferred,
    health: path.health,
    lastOpenedAt: path.lastOpenedAt
  };
}
function validateAccessPathInvariant(path) {
  if (!path.id || !path.id.trim()) {
    throw new Error("AccessPath invariant violated: id must not be empty");
  }
  if (!path.workId || !path.workId.trim()) {
    throw new Error("AccessPath invariant violated: workId must not be empty");
  }
  switch (path.kind) {
    case "web" /* Web */:
      if (!path.url || !path.url.trim()) {
        throw new Error("WebAccessPath invariant violated: url must not be empty");
      }
      break;
    case "local_file" /* LocalFile */:
      if (!path.filePath || !path.filePath.trim()) {
        throw new Error("LocalFileAccessPath invariant violated: filePath must not be empty");
      }
      break;
    case "feed" /* Feed */:
      if (!path.feedUrl || !path.feedUrl.trim()) {
        throw new Error("FeedAccessPath invariant violated: feedUrl must not be empty");
      }
      break;
    case "manual" /* ManualReference */:
      if (!path.description || !path.description.trim()) {
        throw new Error("ManualReferenceAccessPath invariant violated: description must not be empty");
      }
      break;
  }
}
function createWebAccessPath(params) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const health = params.health ?? params.healthState ?? "unknown" /* Unknown */;
  const path = {
    ...params,
    id: params.id ?? newId4(),
    kind: "web" /* Web */,
    health,
    healthState: health,
    capabilities: params.capabilities ?? ["read" /* Read */],
    provenance: params.provenance ?? null,
    lastCheckedAt: params.lastCheckedAt ?? null,
    lastOpenedAt: params.lastOpenedAt ?? null,
    createdAt: now,
    updatedAt: now
  };
  validateAccessPathInvariant(path);
  return path;
}
function createLocalFileAccessPath(params) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const health = params.health ?? params.healthState ?? "local" /* Local */;
  const path = {
    ...params,
    id: params.id ?? newId4(),
    kind: "local_file" /* LocalFile */,
    health,
    healthState: health,
    capabilities: params.capabilities ?? ["read" /* Read */, "local_file" /* LocalFile */],
    provenance: params.provenance ?? null,
    lastCheckedAt: params.lastCheckedAt ?? null,
    lastOpenedAt: params.lastOpenedAt ?? null,
    createdAt: now,
    updatedAt: now
  };
  validateAccessPathInvariant(path);
  return path;
}
function createFeedAccessPath(params) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const health = params.health ?? params.healthState ?? "unknown" /* Unknown */;
  const path = {
    ...params,
    id: params.id ?? newId4(),
    kind: "feed" /* Feed */,
    health,
    healthState: health,
    capabilities: params.capabilities ?? ["updates" /* Updates */, "feed" /* Feed */],
    provenance: params.provenance ?? null,
    lastCheckedAt: params.lastCheckedAt ?? null,
    lastOpenedAt: params.lastOpenedAt ?? null,
    createdAt: now,
    updatedAt: now
  };
  validateAccessPathInvariant(path);
  return path;
}
function createManualAccessPath(params) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const health = params.health ?? params.healthState ?? "local" /* Local */;
  const path = {
    ...params,
    id: params.id ?? newId4(),
    kind: "manual" /* ManualReference */,
    health,
    healthState: health,
    capabilities: params.capabilities ?? [],
    provenance: params.provenance ?? null,
    lastCheckedAt: params.lastCheckedAt ?? null,
    lastOpenedAt: params.lastOpenedAt ?? null,
    createdAt: now,
    updatedAt: now
  };
  validateAccessPathInvariant(path);
  return path;
}
function applyAccessPathPatch(accessPath, patch) {
  const updated = {
    ...accessPath,
    ...patch,
    healthState: patch.health ?? patch.healthState ?? accessPath.healthState,
    health: patch.health ?? patch.healthState ?? accessPath.health,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  validateAccessPathInvariant(updated);
  return updated;
}

// src/entities/memory.ts
import {
  newId as newId5
} from "@grimoire/shared";
var MemoryType = /* @__PURE__ */ ((MemoryType2) => {
  MemoryType2["Diary"] = "diary";
  MemoryType2["Screenshot"] = "screenshot";
  MemoryType2["Panel"] = "panel";
  MemoryType2["Quote"] = "quote";
  MemoryType2["FavoriteChapter"] = "favorite_chapter";
  MemoryType2["FavoritePanel"] = "favorite_panel";
  MemoryType2["Highlight"] = "highlight";
  MemoryType2["ReadingSessionNote"] = "reading_session_note";
  MemoryType2["WhySaved"] = "why_saved";
  MemoryType2["WhyStopped"] = "why_stopped";
  MemoryType2["PersonalNote"] = "personal_note";
  MemoryType2["LoreEntry"] = "lore_entry";
  MemoryType2["Note"] = "note";
  MemoryType2["Review"] = "review";
  MemoryType2["Reaction"] = "reaction";
  return MemoryType2;
})(MemoryType || {});
var MemoryPrivacyState = /* @__PURE__ */ ((MemoryPrivacyState2) => {
  MemoryPrivacyState2["Public"] = "public";
  MemoryPrivacyState2["Normal"] = "normal";
  MemoryPrivacyState2["Private"] = "private";
  MemoryPrivacyState2["Vault"] = "vault";
  return MemoryPrivacyState2;
})(MemoryPrivacyState || {});
function normalizePrivacyState(state) {
  if (state === "vault" /* Vault */ || state === "vault") return "vault" /* Vault */;
  if (state === "private" /* Private */ || state === "private") return "private" /* Private */;
  return "public" /* Public */;
}
var SpoilerLevel = /* @__PURE__ */ ((SpoilerLevel2) => {
  SpoilerLevel2["SpoilerFree"] = "spoiler_free";
  SpoilerLevel2["Mild"] = "mild";
  SpoilerLevel2["Minor"] = "minor";
  SpoilerLevel2["Major"] = "major";
  return SpoilerLevel2;
})(SpoilerLevel || {});
var MemoryAssetType = /* @__PURE__ */ ((MemoryAssetType2) => {
  MemoryAssetType2["Image"] = "image";
  MemoryAssetType2["Video"] = "video";
  MemoryAssetType2["File"] = "file";
  return MemoryAssetType2;
})(MemoryAssetType || {});
function validateMemoryInvariant(candidate) {
  const hasContent = candidate.body && candidate.body.trim().length > 0 || candidate.title && candidate.title.trim().length > 0 || candidate.assets && candidate.assets.length > 0;
  if (!hasContent) {
    throw new Error("Memory invariant violated: memory must have a title, body, or at least one asset");
  }
  if (candidate.progressAnchor) {
    validateProgressAnchor(candidate.progressAnchor);
  }
}
function createMemory(params) {
  const memoryObj = {
    title: params.title,
    body: params.body,
    assets: params.assets ?? [],
    progressAnchor: params.progressAnchor
  };
  validateMemoryInvariant(memoryObj);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    assets: params.assets ?? [],
    tags: params.tags ?? [],
    id: params.id ?? newId5(),
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now
  };
}
function applyMemoryPatch(memory, patch) {
  const updated = {
    ...memory,
    ...patch,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  validateMemoryInvariant(updated);
  return updated;
}
function createMemoryAsset(params) {
  if (!params.fileId || !params.fileId.trim()) {
    throw new Error("MemoryAsset invariant violated: fileId must not be empty");
  }
  return {
    ...params,
    id: params.id ?? newId5(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/capture.ts
import {
  newId as newId6
} from "@grimoire/shared";

// src/use-cases/url-normalizer.ts
var TRACKING_PARAM_PREFIXES = ["utm_", "ga_", "fb_", "_gl", "_ga"];
var TRACKING_PARAMS = /* @__PURE__ */ new Set([
  // Marketing & analytics
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_reader",
  "gclid",
  "gclsrc",
  "dclid",
  "yclid",
  "fbclid",
  "fbc",
  "fbp",
  "twclid",
  "igshid",
  "ttclid",
  "msclkid",
  "mc_cid",
  "mc_eid",
  "_hsenc",
  "_hsmi",
  "vero_id",
  "wickedid",
  // Referrals & tracking affiliates
  "ref",
  "source",
  "referrer",
  "affiliate_id",
  "aff_id",
  "aff",
  "affiliate",
  // Social & share metadata
  "share_id",
  "si",
  "spm",
  "feature",
  // Session identifiers and cache-busting query keys.
  "session_id",
  "jsessionid",
  "phpsessid",
  "_",
  "v",
  "t",
  "timestamp"
]);
function extractRootDomain(hostname) {
  const parts = hostname.toLowerCase().split(".");
  if (parts.length <= 2) {
    return hostname.toLowerCase();
  }
  const secondLast = parts[parts.length - 2];
  if (parts.length >= 3 && secondLast && (secondLast === "co" || secondLast === "com" || secondLast === "org" || secondLast === "net") && parts[parts.length - 1] && parts[parts.length - 1].length === 2) {
    return parts.slice(-3).join(".");
  }
  return parts.slice(-2).join(".");
}
function extractCleanSlug(pathname) {
  const segments = pathname.split("/").map((s) => s.trim()).filter((s) => s.length > 0);
  if (segments.length === 0) {
    return "";
  }
  const rawLast = segments[segments.length - 1] ?? "";
  let decoded = rawLast;
  try {
    decoded = decodeURIComponent(rawLast);
  } catch {
  }
  return decoded.replace(/\.(html?|php|aspx?|jsp)$/i, "");
}
function normalizeUrl(rawUrl) {
  const trimmed = rawUrl.trim();
  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return {
      originalUrl: rawUrl,
      normalizedUrl: "",
      domain: "",
      cleanSlug: "",
      slug: "",
      isValid: false,
      scheme: "",
      hostname: "",
      path: "",
      strippedParams: []
    };
  }
  const protocol = url.protocol.toLowerCase();
  if (protocol !== "http:" && protocol !== "https:") {
    return {
      originalUrl: rawUrl,
      normalizedUrl: "",
      domain: "",
      cleanSlug: "",
      slug: "",
      isValid: false,
      scheme: protocol.replace(/:$/, ""),
      hostname: url.hostname || "",
      path: url.pathname || "",
      strippedParams: []
    };
  }
  const scheme = protocol.slice(0, -1);
  const hostname = url.hostname.toLowerCase();
  const domain = extractRootDomain(hostname);
  url.hash = "";
  if (protocol === "https:" && url.port === "443" || protocol === "http:" && url.port === "80") {
    url.port = "";
  }
  const strippedParams = [];
  const searchParams = new URLSearchParams(url.search);
  const keysToRemove = [];
  for (const [key] of searchParams.entries()) {
    const lowerKey = key.toLowerCase();
    const isTracking = TRACKING_PARAMS.has(lowerKey) || TRACKING_PARAM_PREFIXES.some((prefix) => lowerKey.startsWith(prefix));
    if (isTracking) {
      keysToRemove.push(key);
      strippedParams.push(key);
    }
  }
  for (const key of keysToRemove) {
    searchParams.delete(key);
  }
  searchParams.sort();
  let pathname = url.pathname;
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }
  const cleanSlug = extractCleanSlug(pathname);
  let pathPart = pathname;
  if (pathPart === "/") {
    pathPart = "";
  }
  const queryStr = searchParams.toString();
  const searchPart = queryStr.length > 0 ? `?${queryStr}` : "";
  const portPart = url.port ? `:${url.port}` : "";
  const canonicalUrl = `${protocol}//${hostname}${portPart}${pathPart}${searchPart}`;
  return {
    originalUrl: rawUrl,
    normalizedUrl: canonicalUrl,
    domain,
    cleanSlug,
    slug: cleanSlug,
    isValid: true,
    scheme,
    hostname,
    path: pathname,
    strippedParams
  };
}

// src/entities/capture.ts
var CaptureState = /* @__PURE__ */ ((CaptureState2) => {
  CaptureState2["Pending"] = "pending";
  CaptureState2["Matched"] = "matched";
  CaptureState2["Dismissed"] = "dismissed";
  CaptureState2["Archived"] = "archived";
  return CaptureState2;
})(CaptureState || {});
var CaptureSource = /* @__PURE__ */ ((CaptureSource2) => {
  CaptureSource2["BrowserTabs"] = "browser_tabs";
  CaptureSource2["Bookmarks"] = "bookmarks";
  CaptureSource2["Manual"] = "manual";
  CaptureSource2["Share"] = "share";
  CaptureSource2["Extension"] = "extension";
  return CaptureSource2;
})(CaptureSource || {});
function validateCaptureInvariant(candidate) {
  if (!candidate.url && !candidate.title) {
    throw new Error("Capture invariant violated: must contain at least a url or title");
  }
  if (candidate.confidenceScore !== null && candidate.confidenceScore !== void 0 && (candidate.confidenceScore < 0 || candidate.confidenceScore > 1)) {
    throw new Error(
      `Capture invariant violated: confidenceScore must be between 0.0 and 1.0, got ${candidate.confidenceScore}`
    );
  }
}
function createCapture(params) {
  validateCaptureInvariant(params);
  const normalizedUrl = params.url ? normalizeUrl(params.url).normalizedUrl : null;
  return {
    ...params,
    normalizedUrl: params.normalizedUrl ?? normalizedUrl,
    id: params.id ?? newId6(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}
function applyCapturePatch(capture, patch) {
  const normalizedUrl = patch.url ? normalizeUrl(patch.url).normalizedUrl : capture.normalizedUrl;
  const updated = {
    ...capture,
    ...patch,
    normalizedUrl
  };
  validateCaptureInvariant(updated);
  return updated;
}
function createCaptureBatch(params) {
  if (params.itemCount < 0) {
    throw new Error(`CaptureBatch itemCount must be non-negative, got ${params.itemCount}`);
  }
  if (params.processedCount < 0) {
    throw new Error(`CaptureBatch processedCount must be non-negative, got ${params.processedCount}`);
  }
  return {
    ...params,
    id: params.id ?? newId6(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/archive.ts
import {
  newId as newId7
} from "@grimoire/shared";
var ArchiveReason = /* @__PURE__ */ ((ArchiveReason2) => {
  ArchiveReason2["Completed"] = "completed";
  ArchiveReason2["Dropped"] = "dropped";
  ArchiveReason2["Abandoned"] = "abandoned";
  ArchiveReason2["LostInterest"] = "lost_interest";
  ArchiveReason2["SourceDead"] = "source_dead";
  ArchiveReason2["SourceUnavailable"] = "source_unavailable";
  ArchiveReason2["WaitingIndefinitely"] = "waiting_indefinitely";
  ArchiveReason2["Duplicate"] = "duplicate";
  ArchiveReason2["Merged"] = "merged";
  ArchiveReason2["Migrated"] = "migrated";
  return ArchiveReason2;
})(ArchiveReason || {});
function validateArchiveRecordInvariant(candidate) {
  if (!candidate.workId) {
    throw new Error("ArchiveRecord invariant violated: workId must be specified");
  }
  if (!candidate.reason) {
    throw new Error("ArchiveRecord invariant violated: reason must be specified");
  }
  if (candidate.previousProgress) {
    validateProgressAnchor(candidate.previousProgress);
  }
}
function createArchiveRecord(params) {
  validateArchiveRecordInvariant(params);
  return {
    ...params,
    id: params.id ?? newId7(),
    archivedAt: params.archivedAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/list.ts
import {
  newId as newId8
} from "@grimoire/shared";
function validateUserListInvariant(candidate) {
  if (!candidate.title || !candidate.title.trim()) {
    throw new Error("UserList invariant violated: title must not be empty");
  }
}
function validateListItemInvariant(candidate) {
  if (candidate.position < 0 || !Number.isInteger(candidate.position)) {
    throw new Error(`ListItem invariant violated: position must be a non-negative integer, got ${candidate.position}`);
  }
}
function createUserList(params) {
  validateUserListInvariant(params);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    title: params.title.trim(),
    items: params.items ?? [],
    id: params.id ?? newId8(),
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now
  };
}
function applyUserListPatch(list, patch) {
  const updated = {
    ...list,
    ...patch,
    title: patch.title !== void 0 ? patch.title.trim() : list.title,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  validateUserListInvariant(updated);
  return updated;
}
function createListItem(params) {
  validateListItemInvariant(params);
  return {
    ...params,
    id: params.id ?? newId8(),
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/entities/lore.ts
import {
  newId as newId9
} from "@grimoire/shared";
var LoreEntityType = /* @__PURE__ */ ((LoreEntityType2) => {
  LoreEntityType2["Character"] = "character";
  LoreEntityType2["Faction"] = "faction";
  LoreEntityType2["Place"] = "place";
  LoreEntityType2["Item"] = "item";
  LoreEntityType2["Ability"] = "ability";
  LoreEntityType2["Terminology"] = "terminology";
  LoreEntityType2["Concept"] = "concept";
  return LoreEntityType2;
})(LoreEntityType || {});
function validateLoreEntryInvariant(candidate) {
  if (!candidate.name || !candidate.name.trim()) {
    throw new Error("LoreEntry invariant violated: name must not be empty");
  }
  if (candidate.firstAppearsAnchor) {
    validateProgressAnchor(candidate.firstAppearsAnchor);
  }
}
function createLoreEntry(params) {
  validateLoreEntryInvariant(params);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    name: params.name.trim(),
    id: params.id ?? newId9(),
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now
  };
}
function applyLoreEntryPatch(entry, patch) {
  const updated = {
    ...entry,
    ...patch,
    name: patch.name !== void 0 ? patch.name.trim() : entry.name,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  validateLoreEntryInvariant(updated);
  return updated;
}

// src/entities/publication.ts
import {
  newId as newId10
} from "@grimoire/shared";
function validatePublicationInvariant(candidate) {
  if (!candidate.title || !candidate.title.trim()) {
    throw new Error("Publication invariant violated: title must not be empty");
  }
}
function createPublication(params) {
  validatePublicationInvariant(params);
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    ...params,
    title: params.title.trim(),
    items: params.items ?? [],
    id: params.id ?? newId10(),
    createdAt: params.createdAt ?? now,
    updatedAt: params.updatedAt ?? now
  };
}
function applyPublicationPatch(publication, patch) {
  const updated = {
    ...publication,
    ...patch,
    title: patch.title !== void 0 ? patch.title.trim() : publication.title,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  validatePublicationInvariant(updated);
  return updated;
}
function createPublicationItem(params) {
  if (params.position < 0 || !Number.isInteger(params.position)) {
    throw new Error(`PublicationItem position must be a non-negative integer, got ${params.position}`);
  }
  return {
    ...params,
    id: params.id ?? newId10()
  };
}

// src/entities/work-relation.ts
import {
  newId as newId11
} from "@grimoire/shared";
var WorkRelationType = /* @__PURE__ */ ((WorkRelationType2) => {
  WorkRelationType2["AdaptationOf"] = "adaptation_of";
  WorkRelationType2["OriginalWorkOf"] = "original_work_of";
  WorkRelationType2["Sequel"] = "sequel";
  WorkRelationType2["Prequel"] = "prequel";
  WorkRelationType2["SpinOff"] = "spin_off";
  WorkRelationType2["SideStory"] = "side_story";
  WorkRelationType2["Related"] = "related";
  WorkRelationType2["FanfictionOf"] = "fanfiction_of";
  WorkRelationType2["ParentWorkOf"] = "parent_work_of";
  return WorkRelationType2;
})(WorkRelationType || {});
var RelationType = WorkRelationType;
var INVERSE_RELATION_MAP = {
  ["adaptation_of" /* AdaptationOf */]: "original_work_of" /* OriginalWorkOf */,
  ["original_work_of" /* OriginalWorkOf */]: "adaptation_of" /* AdaptationOf */,
  ["sequel" /* Sequel */]: "prequel" /* Prequel */,
  ["prequel" /* Prequel */]: "sequel" /* Sequel */,
  ["spin_off" /* SpinOff */]: "spin_off" /* SpinOff */,
  ["side_story" /* SideStory */]: "side_story" /* SideStory */,
  ["related" /* Related */]: "related" /* Related */,
  ["fanfiction_of" /* FanfictionOf */]: "parent_work_of" /* ParentWorkOf */,
  ["parent_work_of" /* ParentWorkOf */]: "fanfiction_of" /* FanfictionOf */
};
function getInverseRelationType(relationType) {
  const inverse = INVERSE_RELATION_MAP[relationType];
  if (!inverse) {
    throw new Error(`No inverse mapping found for relation type: ${relationType}`);
  }
  return inverse;
}
function validateWorkRelationInvariant(candidate) {
  if (!candidate.sourceWorkId || !candidate.targetWorkId) {
    throw new Error("WorkRelation invariant violated: sourceWorkId and targetWorkId must be provided");
  }
  if (candidate.sourceWorkId === candidate.targetWorkId) {
    throw new Error("WorkRelation invariant violated: work cannot be related to itself");
  }
}
function createWorkRelation(params) {
  validateWorkRelationInvariant(params);
  const notes = params.notes ?? params.description ?? null;
  return {
    id: params.id ?? newId11(),
    sourceWorkId: params.sourceWorkId,
    targetWorkId: params.targetWorkId,
    relationType: params.relationType,
    notes,
    description: notes,
    createdAt: params.createdAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}
function invertWorkRelation(relation) {
  return {
    ...relation,
    sourceWorkId: relation.targetWorkId,
    targetWorkId: relation.sourceWorkId,
    relationType: getInverseRelationType(relation.relationType)
  };
}

// src/value-objects/external-identifier.ts
function createExternalIdentifier(provider, externalId, url) {
  if (!provider.trim()) throw new Error("ExternalIdentifier: provider must not be empty");
  if (!externalId.trim()) throw new Error("ExternalIdentifier: externalId must not be empty");
  return { provider: provider.trim().toLowerCase(), externalId: externalId.trim(), url };
}

// src/value-objects/update-entry.ts
var UpdateCertainty = /* @__PURE__ */ ((UpdateCertainty2) => {
  UpdateCertainty2["Confirmed"] = "confirmed";
  UpdateCertainty2["Inferred"] = "inferred";
  UpdateCertainty2["Scheduled"] = "scheduled";
  UpdateCertainty2["Unknown"] = "unknown";
  UpdateCertainty2["Stale"] = "stale";
  return UpdateCertainty2;
})(UpdateCertainty || {});
function createUpdateEntry(params) {
  if (!params.label.trim()) throw new Error("UpdateEntry: label must not be empty");
  return {
    ...params,
    label: params.label.trim(),
    detectedAt: params.detectedAt ?? (/* @__PURE__ */ new Date()).toISOString()
  };
}

// src/events/domain-events.ts
import {
  newId as newId12
} from "@grimoire/shared";
function createDomainEvent(type, payload, customId, customTimestamp) {
  return {
    id: customId ?? newId12(),
    type,
    timestamp: customTimestamp ?? (/* @__PURE__ */ new Date()).toISOString(),
    payload
  };
}

// src/return/return-signals.ts
var ReturnSignalType = /* @__PURE__ */ ((ReturnSignalType2) => {
  ReturnSignalType2["ContinueNow"] = "continue_now";
  ReturnSignalType2["CatchUp"] = "catch_up";
  ReturnSignalType2["Stalled"] = "stalled";
  ReturnSignalType2["Waiting"] = "waiting";
  ReturnSignalType2["BingeReady"] = "binge_ready";
  ReturnSignalType2["Rediscover"] = "rediscover";
  return ReturnSignalType2;
})(ReturnSignalType || {});
function createReturnSignal(params) {
  const clampedPriority = Math.max(0, Math.min(100, Math.round(params.priority)));
  return {
    ...params,
    priority: clampedPriority
  };
}
function compareReturnSignals(a, b) {
  return b.priority - a.priority;
}

// src/use-cases/return-engine.ts
var DEFAULT_RETURN_ENGINE_CONFIG = {
  stalledThresholdDays: 14,
  activeRecencyDays: 3,
  bingeReadyThreshold: 5,
  rediscoverThresholdDays: 60
};
function computeReturnSignals(relationships, updates, now, config = DEFAULT_RETURN_ENGINE_CONFIG) {
  const signals = [];
  const nowMs = now.getTime();
  for (const rel of relationships) {
    if (rel.personalState === "archived" /* Archived */ || rel.personalState === "dropped" /* Dropped */) {
      continue;
    }
    const lastActivityDate = rel.lastActiveAt ?? rel.stoppedAt ?? rel.completedAt;
    const daysSinceActivity = lastActivityDate ? Math.max(
      0,
      Math.floor((nowMs - new Date(lastActivityDate).getTime()) / (1e3 * 60 * 60 * 24))
    ) : void 0;
    const workUpdates = updates.get(rel.workId) ?? [];
    const latestUpdate = workUpdates.length > 0 ? [...workUpdates].sort((a, b) => (b.numericIndex ?? 0) - (a.numericIndex ?? 0))[0] : void 0;
    const currentAnchorValue = rel.currentAnchor ? extractNumericProgress(rel.currentAnchor) : null;
    let newMaterialCount;
    if (latestUpdate && latestUpdate.numericIndex !== void 0 && currentAnchorValue !== null) {
      const diff = latestUpdate.numericIndex - currentAnchorValue;
      if (diff > 0) {
        newMaterialCount = Math.max(1, Math.round(diff));
      }
    }
    if (newMaterialCount !== void 0 && newMaterialCount > 0 && (rel.personalState === "reading" /* Reading */ || rel.personalState === "watching" /* Watching */ || rel.personalState === "caught_up" /* CaughtUp */ || rel.personalState === "waiting" /* Waiting */)) {
      const priorityBonus = rel.priority === "urgent" /* Urgent */ ? 15 : rel.priority === "high" /* High */ ? 8 : 0;
      signals.push(
        createReturnSignal({
          type: "catch_up" /* CatchUp */,
          workId: rel.workId,
          explanation: `${newMaterialCount} new chapter${newMaterialCount > 1 ? "s" : ""} available since your last visit (latest: ${latestUpdate?.label})`,
          priority: Math.min(95, 65 + newMaterialCount * 3 + priorityBonus),
          newMaterialCount,
          daysSinceActivity,
          progressAnchor: rel.currentAnchor ?? void 0
        })
      );
      continue;
    }
    if (rel.personalState === "paused" /* Paused */ && newMaterialCount !== void 0 && newMaterialCount >= config.bingeReadyThreshold) {
      signals.push(
        createReturnSignal({
          type: "binge_ready" /* BingeReady */,
          workId: rel.workId,
          explanation: `${newMaterialCount} chapters have accumulated while paused \u2014 ready to binge!`,
          priority: Math.min(90, 70 + newMaterialCount * 2),
          newMaterialCount,
          daysSinceActivity,
          progressAnchor: rel.currentAnchor ?? void 0
        })
      );
      continue;
    }
    if (rel.personalState === "paused" /* Paused */ && daysSinceActivity !== void 0 && daysSinceActivity >= config.rediscoverThresholdDays) {
      signals.push(
        createReturnSignal({
          type: "rediscover" /* Rediscover */,
          workId: rel.workId,
          explanation: `Paused ${daysSinceActivity} days ago \u2014 worth revisiting when you're ready`,
          priority: 35,
          daysSinceActivity,
          progressAnchor: rel.currentAnchor ?? void 0
        })
      );
      continue;
    }
    if (rel.personalState === "reading" /* Reading */ || rel.personalState === "watching" /* Watching */ || rel.personalState === "rereading" /* Rereading */) {
      if (daysSinceActivity !== void 0 && daysSinceActivity <= config.activeRecencyDays) {
        const priorityBonus = rel.priority === "urgent" /* Urgent */ ? 15 : rel.priority === "high" /* High */ ? 8 : 0;
        const anchorText = rel.currentAnchor ? ` at ${formatProgressAnchor(rel.currentAnchor)}` : "";
        signals.push(
          createReturnSignal({
            type: "continue_now" /* ContinueNow */,
            workId: rel.workId,
            explanation: `Pick up where you left off${anchorText}`,
            priority: Math.min(100, 85 + priorityBonus - daysSinceActivity * 3),
            daysSinceActivity,
            progressAnchor: rel.currentAnchor ?? void 0
          })
        );
      } else if (daysSinceActivity !== void 0 && daysSinceActivity >= config.stalledThresholdDays) {
        const anchorText = rel.currentAnchor ? ` at ${formatProgressAnchor(rel.currentAnchor)}` : "";
        signals.push(
          createReturnSignal({
            type: "stalled" /* Stalled */,
            workId: rel.workId,
            explanation: `No progress in ${daysSinceActivity} days \u2014 pick back up${anchorText}`,
            priority: Math.max(
              30,
              Math.min(65, 50 + (rel.priority === "high" /* High */ ? 10 : 0))
            ),
            daysSinceActivity,
            progressAnchor: rel.currentAnchor ?? void 0
          })
        );
      } else {
        signals.push(
          createReturnSignal({
            type: "continue_now" /* ContinueNow */,
            workId: rel.workId,
            explanation: `Currently reading \u2014 active ${daysSinceActivity ?? 0} days ago`,
            priority: 60,
            daysSinceActivity,
            progressAnchor: rel.currentAnchor ?? void 0
          })
        );
      }
      continue;
    }
    if (rel.personalState === "waiting" /* Waiting */ || rel.personalState === "caught_up" /* CaughtUp */) {
      signals.push(
        createReturnSignal({
          type: "waiting" /* Waiting */,
          workId: rel.workId,
          explanation: "Caught up with latest releases \u2014 awaiting new chapter drops",
          priority: 20,
          daysSinceActivity,
          progressAnchor: rel.currentAnchor ?? void 0
        })
      );
      continue;
    }
    if (rel.personalState === "completed" /* Completed */) {
      const daysSinceComplete = rel.completedAt ? Math.floor((nowMs - new Date(rel.completedAt).getTime()) / (1e3 * 60 * 60 * 24)) : void 0;
      if (daysSinceComplete !== void 0 && daysSinceComplete >= config.rediscoverThresholdDays) {
        signals.push(
          createReturnSignal({
            type: "rediscover" /* Rediscover */,
            workId: rel.workId,
            explanation: `Finished ${daysSinceComplete} days ago \u2014 worth reflecting on or revisiting`,
            priority: 35,
            daysSinceActivity: daysSinceComplete,
            progressAnchor: rel.currentAnchor ?? void 0
          })
        );
      }
      continue;
    }
    if (rel.personalState === "planned" /* Planned */ && (rel.priority === "high" /* High */ || rel.priority === "urgent" /* Urgent */)) {
      signals.push(
        createReturnSignal({
          type: "continue_now" /* ContinueNow */,
          workId: rel.workId,
          explanation: "High priority item on your reading list \u2014 ready to start",
          priority: rel.priority === "urgent" /* Urgent */ ? 65 : 50,
          daysSinceActivity
        })
      );
      continue;
    }
  }
  return signals.sort(compareReturnSignals);
}

// src/use-cases/triage-capture.ts
var TriageConfidence = /* @__PURE__ */ ((TriageConfidence2) => {
  TriageConfidence2["Exact"] = "exact";
  TriageConfidence2["High"] = "high";
  TriageConfidence2["Probable"] = "probable";
  TriageConfidence2["Low"] = "low";
  TriageConfidence2["None"] = "none";
  return TriageConfidence2;
})(TriageConfidence || {});
var TriageRecommendation = /* @__PURE__ */ ((TriageRecommendation2) => {
  TriageRecommendation2["AttachToExisting"] = "attach_to_existing";
  TriageRecommendation2["CreateNew"] = "create_new";
  TriageRecommendation2["Discard"] = "discard";
  return TriageRecommendation2;
})(TriageRecommendation || {});
var JUNK_TITLE_PATTERNS = [
  /^404\b/i,
  /^not found\b/i,
  /^page not found\b/i,
  /^access denied\b/i,
  /^forbidden\b/i,
  /^cloudflare\b/i,
  /^just a moment\b/i,
  /^attention required\b/i,
  /^new tab$/i,
  /^untitled$/i,
  /^about:blank$/i,
  /^loading\.\.\.$/i
];
var NOISE_SUFFIX_REGEX = /\s*[-–—|:]\s*(chapter|ep|episode|vol|volume|read|manga|manhwa|scans|dex|reader|online|free|raw|hd).*$/i;
function cleanTitle(raw) {
  let cleaned = raw.replace(NOISE_SUFFIX_REGEX, "");
  cleaned = cleaned.toLowerCase().replace(/[^a-z0-9\s]/gi, " ").replace(/\s+/g, " ").trim();
  return cleaned;
}
function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = i;
    for (let j = 1; j <= b.length; j++) {
      const val = a[i - 1] === b[j - 1] ? row[j - 1] : Math.min(row[j - 1], row[j], prev) + 1;
      row[j - 1] = prev;
      prev = val;
    }
    row[b.length] = prev;
  }
  return row[b.length];
}
function levenshteinSimilarity(a, b) {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshteinDistance(a, b);
  return Math.max(0, 1 - dist / maxLen);
}
function tokenJaccardSimilarity(a, b) {
  const tokensA = new Set(a.split(/\s+/).filter((t) => t.length > 1));
  const tokensB = new Set(b.split(/\s+/).filter((t) => t.length > 1));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const token of tokensA) {
    if (tokensB.has(token)) {
      intersection++;
    }
  }
  const union = tokensA.size + tokensB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}
function triageCaptureItem(capture, existingWorks) {
  const title = capture.title?.trim() ?? "";
  const url = capture.url?.trim() ?? "";
  const isJunkTitle = JUNK_TITLE_PATTERNS.some((pattern) => pattern.test(title));
  if (isJunkTitle || !title && !url) {
    return {
      captureId: capture.id,
      bestMatch: null,
      candidates: [],
      recommendation: "discard" /* Discard */,
      confidence: "high" /* High */,
      explanation: isJunkTitle ? `Page title "${title}" matches a known error, 404, or blank tab pattern` : "Captured item contains neither a title nor a URL"
    };
  }
  const normalizedCaptureUrl = url ? normalizeUrl(url) : null;
  const cleanedCaptureTitle = cleanTitle(title);
  const captureSlug = normalizedCaptureUrl?.slug ? cleanTitle(normalizedCaptureUrl.slug) : "";
  const candidates = [];
  for (const work of existingWorks) {
    const reasons = [];
    let bestScore = 0;
    const titlesToCheck = [
      { title: work.title, type: "primary" },
      ...work.alternativeTitles.map((t) => ({ title: t, type: "alternative" })),
      ...work.aliases.map((a) => ({ title: a, type: "alias" }))
    ];
    for (const item of titlesToCheck) {
      const cleanedTarget = cleanTitle(item.title);
      if (!cleanedTarget) continue;
      if (cleanedCaptureTitle === cleanedTarget) {
        bestScore = Math.max(bestScore, 1);
        reasons.push(`Exact match with ${item.type} title "${item.title}"`);
        break;
      }
      if (cleanedCaptureTitle.length >= 4 && cleanedTarget.includes(cleanedCaptureTitle) || cleanedTarget.length >= 4 && cleanedCaptureTitle.includes(cleanedTarget)) {
        const subScore = 0.85;
        if (subScore > bestScore) {
          bestScore = subScore;
          reasons.push(`Substring match with ${item.type} title "${item.title}"`);
        }
      }
      const lev = levenshteinSimilarity(cleanedCaptureTitle, cleanedTarget);
      if (lev >= 0.75 && lev > bestScore) {
        bestScore = lev;
        reasons.push(
          `High character similarity (${Math.round(lev * 100)}%) with ${item.type} title "${item.title}"`
        );
      }
      const jaccard = tokenJaccardSimilarity(cleanedCaptureTitle, cleanedTarget);
      if (jaccard >= 0.6 && jaccard > bestScore) {
        bestScore = Math.max(bestScore, jaccard);
        reasons.push(
          `Token overlap (${Math.round(jaccard * 100)}%) with ${item.type} title "${item.title}"`
        );
      }
      if (captureSlug) {
        const slugLev = levenshteinSimilarity(captureSlug, cleanedTarget);
        if (slugLev >= 0.8 && slugLev * 0.9 > bestScore) {
          bestScore = slugLev * 0.9;
          reasons.push(`URL slug "${captureSlug}" matched ${item.type} title "${item.title}"`);
        }
      }
    }
    if (bestScore >= 0.2) {
      let confidence;
      if (bestScore >= 0.95) confidence = "exact" /* Exact */;
      else if (bestScore >= 0.8) confidence = "high" /* High */;
      else if (bestScore >= 0.5) confidence = "probable" /* Probable */;
      else confidence = "low" /* Low */;
      candidates.push({
        workId: work.id,
        workTitle: work.title,
        score: Math.round(bestScore * 100) / 100,
        confidence,
        matchReasons: reasons
      });
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  const bestMatch = candidates[0] ?? null;
  if (bestMatch && (bestMatch.confidence === "exact" /* Exact */ || bestMatch.confidence === "high" /* High */)) {
    return {
      captureId: capture.id,
      bestMatch,
      candidates,
      recommendation: "attach_to_existing" /* AttachToExisting */,
      confidence: bestMatch.confidence,
      explanation: `Strong match with existing work "${bestMatch.workTitle}" (${bestMatch.matchReasons[0] ?? "similarity score " + bestMatch.score})`
    };
  }
  if (bestMatch && bestMatch.confidence === "probable" /* Probable */) {
    return {
      captureId: capture.id,
      bestMatch,
      candidates,
      recommendation: "attach_to_existing" /* AttachToExisting */,
      confidence: "probable" /* Probable */,
      explanation: `Probable match with "${bestMatch.workTitle}" (${bestMatch.matchReasons[0] ?? "partial match"}), user verification advised`
    };
  }
  return {
    captureId: capture.id,
    bestMatch: null,
    candidates,
    recommendation: "create_new" /* CreateNew */,
    confidence: candidates.length > 0 ? "low" /* Low */ : "none" /* None */,
    explanation: candidates.length > 0 ? `No strong match found (closest was "${candidates[0]?.workTitle}" at ${candidates[0]?.score}). Recommend creating a new work.` : "No similar works found in your Vault. Recommend adding as a new work."
  };
}

// src/use-cases/update-checker.ts
function evaluateNewRelease(currentProgress, newRelease) {
  if (!currentProgress) {
    return {
      isUnread: true,
      unreadCount: Math.max(1, Math.round(newRelease.chapterNumber))
    };
  }
  switch (currentProgress.type) {
    case "chapter": {
      const currentChapter = currentProgress.chapter + (currentProgress.subchapter ? currentProgress.subchapter * 0.1 : 0);
      const diff = newRelease.chapterNumber - currentChapter;
      if (diff > 0) {
        return {
          isUnread: true,
          unreadCount: Math.max(1, Math.round(diff))
        };
      }
      return { isUnread: false, unreadCount: 0 };
    }
    case "volume_chapter": {
      const currentChapter = currentProgress.chapter;
      const diff = newRelease.chapterNumber - currentChapter;
      if (diff > 0) {
        return {
          isUnread: true,
          unreadCount: Math.max(1, Math.round(diff))
        };
      }
      return { isUnread: false, unreadCount: 0 };
    }
    case "episode": {
      const currentEpisode = currentProgress.episode;
      const diff = newRelease.chapterNumber - currentEpisode;
      if (diff > 0) {
        return {
          isUnread: true,
          unreadCount: Math.max(1, Math.round(diff))
        };
      }
      return { isUnread: false, unreadCount: 0 };
    }
    case "percent": {
      if (currentProgress.percent >= 100) {
        return { isUnread: false, unreadCount: 0 };
      }
      return { isUnread: true, unreadCount: 1 };
    }
    case "page":
    case "epub_locator":
    case "provider":
    case "audiobook": {
      const numeric = extractNumericProgress(currentProgress);
      if (numeric !== null) {
        const diff = newRelease.chapterNumber - numeric;
        if (diff > 0) {
          return {
            isUnread: true,
            unreadCount: Math.max(1, Math.round(diff))
          };
        }
        return { isUnread: false, unreadCount: 0 };
      }
      return { isUnread: true, unreadCount: 1 };
    }
  }
}
function evaluateBatchReleases(currentProgress, releases) {
  if (releases.length === 0) {
    return { hasUnread: false, totalUnreadCount: 0, latestRelease: null };
  }
  const sorted = [...releases].sort((a, b) => b.chapterNumber - a.chapterNumber);
  const latestRelease = sorted[0] ?? null;
  if (!latestRelease) {
    return { hasUnread: false, totalUnreadCount: 0, latestRelease: null };
  }
  const evalResult = evaluateNewRelease(currentProgress, latestRelease);
  return {
    hasUnread: evalResult.isUnread,
    totalUnreadCount: evalResult.unreadCount,
    latestRelease
  };
}

// src/use-cases/source-health-policy.ts
var MINUTE = 6e4;
var HOUR = 60 * MINUTE;
var JITTER_RATIO = 0.2;
var SUSPEND_AFTER_FAILURES = 5;
function parseTimestamp(value, label) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/.exec(
    value
  );
  if (!match) throw new Error(`${label} must be a valid ISO 8601 timestamp`);
  const [, year, month, day, hour, minute, second, zone] = match;
  const yearNumber = Number(year);
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  const hourNumber = Number(hour);
  const minuteNumber = Number(minute);
  const secondNumber = Number(second);
  const calendarDate = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));
  const validDate = calendarDate.getUTCFullYear() === yearNumber && calendarDate.getUTCMonth() === monthNumber - 1 && calendarDate.getUTCDate() === dayNumber;
  const zoneMatch = /^([+-])(\d{2}):(\d{2})$/.exec(zone);
  const validZone = zone === "Z" || zoneMatch !== null && Number(zoneMatch[2]) <= 23 && Number(zoneMatch[3]) <= 59;
  const timestamp = Date.parse(value);
  if (!validDate || hourNumber > 23 || minuteNumber > 59 || secondNumber > 59 || !validZone || !Number.isFinite(timestamp)) {
    throw new Error(`${label} must be a valid ISO 8601 timestamp`);
  }
  return timestamp;
}
function validateState(state) {
  if (!Number.isInteger(state.consecutiveFailures) || state.consecutiveFailures < 0) {
    throw new Error("HealthMonitorState consecutiveFailures must be a non-negative integer");
  }
  if (state.lastCheckedAt !== null) parseTimestamp(state.lastCheckedAt, "lastCheckedAt");
  if (state.lastSuccessfulAt !== null) {
    parseTimestamp(state.lastSuccessfulAt, "lastSuccessfulAt");
  }
}
function ensureMonotonicCheck(state, checkedAt) {
  const timestamp = parseTimestamp(checkedAt, "checkedAt");
  if (state.lastCheckedAt !== null && timestamp < parseTimestamp(state.lastCheckedAt, "lastCheckedAt")) {
    throw new Error("checkedAt must not precede lastCheckedAt");
  }
}
function createHealthMonitorState(health = "unknown" /* Unknown */) {
  return {
    health,
    consecutiveFailures: 0,
    lastCheckedAt: null,
    lastSuccessfulAt: null,
    nextCheckAt: null,
    autoPollingSuspended: false
  };
}
function recordHealthCheckFailure(state, failedAt, random = Math.random) {
  validateState(state);
  ensureMonotonicCheck(state, failedAt);
  const consecutiveFailures = state.consecutiveFailures + 1;
  const sample = random();
  if (!Number.isFinite(sample) || sample < 0 || sample > 1) {
    throw new Error("random must return a number between 0 and 1");
  }
  if (consecutiveFailures >= SUSPEND_AFTER_FAILURES) {
    return {
      ...state,
      health: "broken" /* Broken */,
      consecutiveFailures,
      lastCheckedAt: failedAt,
      nextCheckAt: null,
      autoPollingSuspended: true
    };
  }
  const baseDelay = consecutiveFailures === 1 ? 15 * MINUTE : consecutiveFailures === 2 ? HOUR : 6 * HOUR;
  const retryAfterMs = Math.round(baseDelay * (1 - JITTER_RATIO + sample * 2 * JITTER_RATIO));
  const health = consecutiveFailures >= 3 ? "degraded" /* Degraded */ : state.health;
  return {
    ...state,
    health,
    consecutiveFailures,
    lastCheckedAt: failedAt,
    nextCheckAt: new Date(Date.parse(failedAt) + retryAfterMs).toISOString(),
    autoPollingSuspended: false
  };
}
function recordHealthCheckSuccess(state, checkedAt) {
  validateState(state);
  ensureMonotonicCheck(state, checkedAt);
  return {
    health: state.health === "local" /* Local */ ? "local" /* Local */ : "healthy" /* Healthy */,
    consecutiveFailures: 0,
    lastCheckedAt: checkedAt,
    lastSuccessfulAt: checkedAt,
    nextCheckAt: null,
    autoPollingSuspended: false
  };
}
function createConditionalRequestHeaders(validators) {
  const headers = {};
  if (validators.etag?.trim()) headers["If-None-Match"] = validators.etag;
  if (validators.lastModified?.trim()) headers["If-Modified-Since"] = validators.lastModified;
  return headers;
}
function carryForwardConditionalValidators(previous, response) {
  const etag = response.status === 304 ? response.etag === void 0 ? previous.etag : response.etag : response.etag ?? null;
  const lastModified = response.status === 304 ? response.lastModified === void 0 ? previous.lastModified : response.lastModified : response.lastModified ?? null;
  return { etag, lastModified };
}

// src/use-cases/update-certainty.ts
var ISO_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;
function parseTimestamp2(value, label) {
  const match = ISO_TIMESTAMP.exec(value);
  if (!match) throw new Error(`${label} must be a valid ISO 8601 timestamp`);
  const [, year, month, day, hour, minute, second, zone] = match;
  const yearNumber = Number(year);
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  const date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));
  const zoneMatch = /^([+-])(\d{2}):(\d{2})$/.exec(zone);
  const validZone = zone === "Z" || zoneMatch !== null && Number(zoneMatch[2]) <= 23 && Number(zoneMatch[3]) <= 59;
  const timestamp = Date.parse(value);
  if (date.getUTCFullYear() !== yearNumber || date.getUTCMonth() !== monthNumber - 1 || date.getUTCDate() !== dayNumber || Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59 || !validZone || !Number.isFinite(timestamp)) {
    throw new Error(`${label} must be a valid ISO 8601 timestamp`);
  }
  return timestamp;
}
function applyUpdateCertaintyValidity(entry, evaluatedAt, validityWindowMs) {
  if (!Number.isFinite(validityWindowMs) || validityWindowMs < 0) {
    throw new Error("validityWindowMs must be a non-negative finite number");
  }
  const detectedAt = parseTimestamp2(entry.detectedAt, "detectedAt");
  const evaluationTime = parseTimestamp2(evaluatedAt, "evaluatedAt");
  const isStale = evaluationTime - detectedAt >= validityWindowMs;
  if (!isStale || entry.certainty === "stale" /* Stale */) return entry;
  return { ...entry, certainty: "stale" /* Stale */ };
}

// src/use-cases/select-access-path.ts
function selectFallbackAccessPath(paths, preferredAccessPathId) {
  const preferred = paths.find((path) => path.id === preferredAccessPathId);
  if (preferred?.health === "healthy" /* Healthy */) return preferred;
  const healthyAlternate = paths.find(
    (path) => path.id !== preferredAccessPathId && path.health === "healthy" /* Healthy */
  );
  if (healthyAlternate) return healthyAlternate;
  return paths.find((path) => path.kind === "local_file" /* LocalFile */) ?? null;
}

// src/use-cases/health-scheduler.ts
function applyHealthCheckResult(path, probe, checkedAt = (/* @__PURE__ */ new Date()).toISOString(), random = Math.random) {
  const currentState = path.healthMonitorState ?? createHealthMonitorState(path.health);
  let newState;
  if (probe.ok) {
    newState = recordHealthCheckSuccess(currentState, checkedAt);
  } else {
    newState = recordHealthCheckFailure(currentState, checkedAt, random);
  }
  const updatedPath = {
    ...path,
    health: newState.health,
    healthState: newState.health,
    lastCheckedAt: checkedAt,
    healthMonitorState: newState,
    updatedAt: checkedAt
  };
  return { updatedPath, monitorState: newState };
}
function isEligibleForHealthCheck(path, now = (/* @__PURE__ */ new Date()).toISOString()) {
  if (path.kind === "local_file" || path.kind === "manual") {
    return false;
  }
  const state = path.healthMonitorState ?? createHealthMonitorState(path.health);
  if (state.autoPollingSuspended) {
    return false;
  }
  if (state.nextCheckAt !== null) {
    const nextCheckTime = Date.parse(state.nextCheckAt);
    const currentTime = Date.parse(now);
    if (Number.isFinite(nextCheckTime) && currentTime < nextCheckTime) {
      return false;
    }
  }
  return true;
}
var HealthScheduler = class {
  probe;
  onHealthChange;
  intervalMs;
  random;
  registeredPaths = /* @__PURE__ */ new Map();
  validators = /* @__PURE__ */ new Map();
  activeAbortControllers = /* @__PURE__ */ new Map();
  timer = null;
  isRunning = false;
  constructor(options) {
    this.probe = options.probe;
    this.onHealthChange = options.onHealthChange;
    this.intervalMs = options.intervalMs ?? 6e4;
    this.random = options.random ?? Math.random;
  }
  /**
   * Register an AccessPath with the scheduler.
   */
  register(path, initialValidators) {
    this.registeredPaths.set(path.id, path);
    if (initialValidators) {
      this.validators.set(path.id, initialValidators);
    }
  }
  /**
   * Unregister an AccessPath from the scheduler and cancel any ongoing check.
   */
  unregister(pathId) {
    this.cancel(pathId);
    this.registeredPaths.delete(pathId);
    this.validators.delete(pathId);
  }
  /**
   * Cancel an in-flight check for a specific AccessPath.
   */
  cancel(pathId) {
    const controller = this.activeAbortControllers.get(pathId);
    if (controller) {
      controller.abort();
      this.activeAbortControllers.delete(pathId);
    }
  }
  /**
   * Cancel all in-flight checks.
   */
  cancelAll() {
    for (const [pathId, controller] of this.activeAbortControllers.entries()) {
      controller.abort();
    }
    this.activeAbortControllers.clear();
  }
  /**
   * Start the periodic background scheduler.
   */
  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.timer = setInterval(() => {
      void this.tick();
    }, this.intervalMs);
  }
  /**
   * Stop the scheduler and cancel all in-flight checks.
   */
  stop() {
    if (!this.isRunning) return;
    this.isRunning = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.cancelAll();
  }
  /**
   * Execute an on-demand check for a specific AccessPath, cancelling any existing check.
   */
  async checkNow(pathId, options = {}) {
    const path = this.registeredPaths.get(pathId);
    if (!path) return null;
    return this.executeCheck(path, options);
  }
  /**
   * Internal scheduler tick: finds all eligible paths and checks them.
   */
  async tick(now = (/* @__PURE__ */ new Date()).toISOString()) {
    const outcomes = [];
    const eligiblePaths = [];
    for (const path of this.registeredPaths.values()) {
      if (isEligibleForHealthCheck(path, now) && !this.activeAbortControllers.has(path.id)) {
        eligiblePaths.push(path);
      }
    }
    const checkPromises = eligiblePaths.map(async (path) => {
      try {
        const outcome = await this.executeCheck(path, { now });
        if (outcome) outcomes.push(outcome);
      } catch {
      }
    });
    await Promise.all(checkPromises);
    return outcomes;
  }
  /**
   * Execute a single check with cancellation and conditional validator headers.
   */
  async executeCheck(path, options) {
    const checkedAt = options.now ?? (/* @__PURE__ */ new Date()).toISOString();
    const previousState = path.healthMonitorState ?? createHealthMonitorState(path.health);
    this.cancel(path.id);
    const controller = new AbortController();
    this.activeAbortControllers.set(path.id, controller);
    const currentValidators = this.validators.get(path.id) ?? { etag: null, lastModified: null };
    const headers = createConditionalRequestHeaders(currentValidators);
    let probeResult;
    try {
      probeResult = await this.probe(path, controller.signal, headers);
    } catch (err) {
      if (controller.signal.aborted) {
        probeResult = {
          ok: false,
          errorMessage: "Health check aborted"
        };
      } else {
        probeResult = {
          ok: false,
          errorMessage: err instanceof Error ? err.message : String(err)
        };
      }
    } finally {
      this.activeAbortControllers.delete(path.id);
    }
    if (probeResult.status) {
      const responseVal = {
        status: probeResult.status,
        etag: probeResult.etag,
        lastModified: probeResult.lastModified
      };
      const updatedValidators = carryForwardConditionalValidators(currentValidators, responseVal);
      this.validators.set(path.id, updatedValidators);
    }
    const { updatedPath, monitorState } = applyHealthCheckResult(
      path,
      probeResult,
      checkedAt,
      options.random ?? this.random
    );
    this.registeredPaths.set(path.id, updatedPath);
    const outcome = {
      path: updatedPath,
      previousState,
      newState: monitorState,
      probeResult,
      checkedAt
    };
    this.onHealthChange?.(outcome);
    return outcome;
  }
  getRegisteredPath(pathId) {
    return this.registeredPaths.get(pathId);
  }
  getRegisteredCount() {
    return this.registeredPaths.size;
  }
  getActiveCheckCount() {
    return this.activeAbortControllers.size;
  }
  getValidators(pathId) {
    return this.validators.get(pathId);
  }
};

// src/use-cases/archive-work.ts
import { newId as newId13 } from "@grimoire/shared";
function archiveWork(params) {
  const now = params.archivedAt ?? (/* @__PURE__ */ new Date()).toISOString();
  const trimmedWhyStopped = params.whyStopped?.trim() || null;
  const trimmedNote = params.note?.trim() || null;
  if (!params.work || !params.work.id) {
    throw new Error("Archive invariant violated: valid work must be specified");
  }
  let targetState = "archived" /* Archived */;
  if (params.reason === "completed" /* Completed */) {
    targetState = "completed" /* Completed */;
  } else if (params.reason === "dropped" /* Dropped */) {
    targetState = "dropped" /* Dropped */;
  }
  let updatedRelationship = null;
  if (params.relationship) {
    updatedRelationship = applyReadingRelationshipPatch(params.relationship, {
      personalState: targetState,
      stoppedAt: params.relationship.stoppedAt ?? now,
      stoppedReason: params.reason,
      stoppedNote: trimmedWhyStopped ?? trimmedNote ?? params.relationship.stoppedNote,
      // INVARIANT: currentAnchor is strictly preserved and never nulled out
      currentAnchor: params.relationship.currentAnchor,
      completedAt: params.reason === "completed" /* Completed */ ? params.relationship.completedAt ?? now : params.relationship.completedAt
    });
  }
  let whyStoppedMemory = null;
  const shouldCreateMemory = params.createWhyStoppedMemory ?? true;
  if (trimmedWhyStopped && shouldCreateMemory) {
    whyStoppedMemory = createMemory({
      workId: params.work.id,
      type: "why_stopped" /* WhyStopped */,
      title: `Archive Reflection (${params.reason})`,
      body: trimmedWhyStopped,
      privacyState: params.work.privacyState,
      spoilerLevel: "spoiler_free" /* SpoilerFree */,
      showInGlobalGallery: false,
      accessPathId: null,
      progressAnchor: params.relationship?.currentAnchor ?? null,
      createdAt: now
    });
  }
  const existingMemories = params.memories ?? [];
  const finalMemories = whyStoppedMemory ? [...existingMemories, whyStoppedMemory] : [...existingMemories];
  const finalAccessPaths = params.accessPaths ? [...params.accessPaths] : [];
  const archiveRecord = createArchiveRecord({
    id: newId13(),
    workId: params.work.id,
    reason: params.reason,
    note: trimmedNote,
    whyStopped: trimmedWhyStopped,
    previousState: params.relationship?.personalState ?? null,
    previousProgress: params.relationship?.currentAnchor ?? null,
    archivedAt: now,
    mergedIntoWorkId: params.mergedIntoWorkId ?? null,
    metadata: params.metadata ?? null
  });
  const updatedWork = applyWorkPatch(params.work, {
    deletedAt: now,
    ...params.mergedIntoWorkId ? { mergedIntoWorkId: params.mergedIntoWorkId } : {}
  });
  const event = createDomainEvent("work.archived", {
    workId: params.work.id,
    reason: params.reason,
    note: trimmedNote ?? void 0
  }, void 0, now);
  return {
    work: updatedWork,
    archiveRecord,
    relationship: updatedRelationship,
    accessPaths: finalAccessPaths,
    memories: finalMemories,
    whyStoppedMemory,
    event
  };
}

// src/use-cases/find-duplicate-candidates.ts
var ROMAN_NUMERALS = {
  i: "1",
  ii: "2",
  iii: "3",
  iv: "4",
  v: "5",
  vi: "6",
  vii: "7",
  viii: "8",
  ix: "9",
  x: "10"
};
function stripSubtitles(title) {
  let cleaned = title.trim();
  cleaned = cleaned.replace(/\s*(\(|\[)(manga|webtoon|anime|tv|light novel|novel|comic|manhwa|manhua|official|dub|sub|color|uncensored|serial|adaptation)[^()\]]*(\)|\])/gi, " ");
  cleaned = cleaned.replace(/\s*(\(|\[)(season|part|cour|vol\.?)\s*\d+[^()\]]*(\)|\])/gi, " ");
  const delimiterMatch = cleaned.match(/\s+[:\-~|—–]\s+(.+)$/);
  if (delimiterMatch && delimiterMatch.index && delimiterMatch.index > 2) {
    cleaned = cleaned.slice(0, delimiterMatch.index);
  } else if (cleaned.includes(": ")) {
    const colonIdx = cleaned.indexOf(": ");
    if (colonIdx > 2) {
      cleaned = cleaned.slice(0, colonIdx);
    }
  }
  cleaned = cleaned.replace(/\s+(season\s*\d+|\d+(st|nd|rd|th)\s*season|part\s*\d+|cour\s*\d+|vol(ume)?\.?\s*\d+)$/gi, "");
  return cleaned.trim();
}
function normalizeTitleForDeduplication(title) {
  if (!title) return "";
  let norm = title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  norm = norm.replace(/\s*&\s*/g, " and ");
  norm = norm.replace(/['’]s\b/g, "");
  norm = norm.replace(/[^a-z0-9\s]/g, " ");
  const rawTokens = norm.split(/\s+/).filter(Boolean);
  if (rawTokens.length === 0) return "";
  const nonArticles = rawTokens.filter((t) => t !== "the" && t !== "a" && t !== "an");
  const tokensToUse = nonArticles.length > 0 ? nonArticles : rawTokens;
  const lemmatized = tokensToUse.map((token) => {
    if (ROMAN_NUMERALS[token]) {
      return ROMAN_NUMERALS[token];
    }
    if (token.length > 3 && token.endsWith("s") && !token.endsWith("ss") && !token.endsWith("us") && !token.endsWith("is")) {
      return token.slice(0, -1);
    }
    return token;
  });
  return lemmatized.join(" ").trim();
}
function titleLevenshteinSimilarity(a, b) {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const m = a.length;
  const n = b.length;
  const maxLen = Math.max(m, n);
  if (maxLen === 0) return 1;
  const dp = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const current = Math.min(dp[j] + 1, prev + 1, dp[j - 1] + cost);
      dp[j - 1] = prev;
      prev = current;
    }
    dp[n] = prev;
  }
  return Math.max(0, 1 - dp[n] / maxLen);
}
function normalizeProviderName(provider) {
  const p = provider.trim().toLowerCase();
  if (p === "myanimelist" || p === "my_anime_list") return "mal";
  return p;
}
function compareWorksForDuplicates(workA, workB, threshold = 0.4) {
  const reasons = [];
  let bestConfidence = 0;
  for (const extA of workA.externalIds) {
    const provA = normalizeProviderName(extA.provider);
    const idA = extA.externalId.trim().toLowerCase();
    for (const extB of workB.externalIds) {
      const provB = normalizeProviderName(extB.provider);
      const idB = extB.externalId.trim().toLowerCase();
      if (provA === provB && idA === idB) {
        bestConfidence = 1;
        reasons.push(`Shared External ID: ${extA.provider.toLowerCase().trim()}:${extA.externalId.trim()}`);
      }
    }
  }
  const rawTitleA = workA.title.trim();
  const rawTitleB = workB.title.trim();
  if (rawTitleA.toLowerCase() === rawTitleB.toLowerCase()) {
    bestConfidence = Math.max(bestConfidence, 1);
    reasons.push("Exact Title Match");
  }
  const normTitleA = normalizeTitleForDeduplication(rawTitleA);
  const normTitleB = normalizeTitleForDeduplication(rawTitleB);
  if (normTitleA && normTitleB) {
    if (normTitleA === normTitleB) {
      bestConfidence = Math.max(bestConfidence, 0.95);
      if (!reasons.includes("Exact Title Match")) {
        reasons.push("Normalized Title Match");
      }
    } else {
      const baseA = normalizeTitleForDeduplication(stripSubtitles(rawTitleA));
      const baseB = normalizeTitleForDeduplication(stripSubtitles(rawTitleB));
      if (baseA && baseB && baseA === baseB && baseA.length >= 3) {
        bestConfidence = Math.max(bestConfidence, 0.9);
        if (!reasons.includes("Normalized Title Match") && !reasons.includes("Exact Title Match")) {
          reasons.push("Normalized Title Match");
        }
      }
    }
  }
  const altTitlesA = workA.alternativeTitles.map((t) => t.trim());
  const altTitlesB = workB.alternativeTitles.map((t) => t.trim());
  let hasAltMatch = false;
  const titlesA = [rawTitleA, ...altTitlesA];
  const titlesB = [rawTitleB, ...altTitlesB];
  for (const tA of titlesA) {
    const normA = normalizeTitleForDeduplication(tA);
    if (!normA) continue;
    for (const tB of titlesB) {
      if (tA === rawTitleA && tB === rawTitleB) continue;
      const normB = normalizeTitleForDeduplication(tB);
      if (normA === normB) {
        hasAltMatch = true;
        bestConfidence = Math.max(bestConfidence, 0.9);
        break;
      }
    }
    if (hasAltMatch) break;
  }
  if (hasAltMatch && !reasons.includes("Alternative Title Match")) {
    reasons.push("Alternative Title Match");
  }
  const aliasesA = workA.aliases.map((a) => a.trim().toLowerCase());
  const aliasesB = workB.aliases.map((a) => a.trim().toLowerCase());
  let hasAliasMatch = false;
  for (const aliasA of aliasesA) {
    if (!aliasA) continue;
    if (aliasesB.includes(aliasA)) {
      hasAliasMatch = true;
      break;
    }
    if (aliasA === rawTitleB.toLowerCase() || aliasA === normTitleB) {
      hasAliasMatch = true;
      break;
    }
  }
  if (!hasAliasMatch) {
    for (const aliasB of aliasesB) {
      if (!aliasB) continue;
      if (aliasB === rawTitleA.toLowerCase() || aliasB === normTitleA) {
        hasAliasMatch = true;
        break;
      }
    }
  }
  if (hasAliasMatch) {
    bestConfidence = Math.max(bestConfidence, 0.88);
    if (!reasons.includes("Alias Match")) {
      reasons.push("Alias Match");
    }
  }
  if (bestConfidence < 0.8 && normTitleA && normTitleB) {
    const sim = titleLevenshteinSimilarity(normTitleA, normTitleB);
    if (sim >= 0.82) {
      bestConfidence = Math.max(bestConfidence, Math.round(sim * 100) / 100);
      reasons.push("High Title Similarity");
    }
  }
  const roundedScore = Math.round(bestConfidence * 100) / 100;
  return {
    confidence: roundedScore,
    matchReasons: reasons,
    isDuplicate: roundedScore >= threshold && reasons.length > 0
  };
}
function findDuplicateCandidates(worksOrTarget, poolOrOptions, maybeOptions) {
  if (Array.isArray(worksOrTarget)) {
    const works = worksOrTarget;
    const options = poolOrOptions ?? {};
    return findDuplicatesInList(works, options);
  } else {
    const target = worksOrTarget;
    const pool = poolOrOptions ?? [];
    const options = maybeOptions ?? {};
    return findDuplicatesForTarget(target, pool, options);
  }
}
function findDuplicatesInList(works, options) {
  const minConfidence = options.minConfidence ?? 0.4;
  const includeArchived = options.includeArchived ?? false;
  const eligible = works.filter((w) => {
    if (includeArchived) return true;
    return w.deletedAt === null && !w.mergedIntoWorkId;
  });
  const candidates = [];
  for (let i = 0; i < eligible.length; i++) {
    for (let j = i + 1; j < eligible.length; j++) {
      const workA = eligible[i];
      const workB = eligible[j];
      if (workA.id === workB.id) continue;
      const result = compareWorksForDuplicates(workA, workB, minConfidence);
      if (result.isDuplicate) {
        candidates.push({
          workA,
          workB,
          confidence: result.confidence,
          matchReasons: result.matchReasons
        });
      }
    }
  }
  candidates.sort((a, b) => {
    if (b.confidence !== a.confidence) {
      return b.confidence - a.confidence;
    }
    return a.workA.title.localeCompare(b.workA.title);
  });
  if (options.limit && options.limit > 0) {
    return candidates.slice(0, options.limit);
  }
  return candidates;
}
function findDuplicatesForTarget(target, pool, options) {
  const minConfidence = options.minConfidence ?? 0.4;
  const includeArchived = options.includeArchived ?? false;
  const candidates = [];
  for (const candidateWork of pool) {
    if (candidateWork.id === target.id) continue;
    if (!includeArchived && (candidateWork.deletedAt !== null || candidateWork.mergedIntoWorkId)) {
      continue;
    }
    const result = compareWorksForDuplicates(target, candidateWork, minConfidence);
    if (result.isDuplicate) {
      candidates.push({
        workA: target,
        workB: candidateWork,
        confidence: result.confidence,
        matchReasons: result.matchReasons
      });
    }
  }
  candidates.sort((a, b) => b.confidence - a.confidence);
  if (options.limit && options.limit > 0) {
    return candidates.slice(0, options.limit);
  }
  return candidates;
}

// src/use-cases/merge-works-non-destructive.ts
import { newId as newId14 } from "@grimoire/shared";
function combineStringArrays(primary, secondary) {
  const seen = /* @__PURE__ */ new Set();
  const combined = [];
  for (const item of [...primary, ...secondary]) {
    const trimmed = item.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      combined.push(trimmed);
    }
  }
  return combined;
}
var PRIORITY_RANKS = {
  ["urgent" /* Urgent */]: 4,
  ["high" /* High */]: 3,
  ["normal" /* Normal */]: 2,
  ["low" /* Low */]: 1
};
function pickHigherPriority(a, b) {
  return (PRIORITY_RANKS[a] ?? 0) >= (PRIORITY_RANKS[b] ?? 0) ? a : b;
}
function mergeWorksNonDestructive(params) {
  const { targetWork, secondaryWork, progressPolicy } = params;
  const now = params.mergedAt ?? (/* @__PURE__ */ new Date()).toISOString();
  if (targetWork.id === secondaryWork.id) {
    throw new Error("Merge invariant violated: cannot merge a work into itself");
  }
  const combinedAliases = combineStringArrays(targetWork.aliases, secondaryWork.aliases);
  const secondaryTitleToAdd = secondaryWork.title.trim().toLowerCase() !== targetWork.title.trim().toLowerCase() ? [secondaryWork.title.trim()] : [];
  const combinedAltTitles = combineStringArrays(
    targetWork.alternativeTitles,
    [...secondaryTitleToAdd, ...secondaryWork.alternativeTitles]
  );
  const combinedTags = combineStringArrays(targetWork.tags, secondaryWork.tags);
  const combinedGenres = combineStringArrays(targetWork.genres, secondaryWork.genres);
  const seenExtIds = /* @__PURE__ */ new Set();
  const combinedExternalIds = [];
  for (const ext of [...targetWork.externalIds, ...secondaryWork.externalIds]) {
    const key = `${ext.provider.toLowerCase().trim()}:${ext.externalId.toLowerCase().trim()}`;
    if (!seenExtIds.has(key)) {
      seenExtIds.add(key);
      combinedExternalIds.push(ext);
    }
  }
  let resolvedPrivacy = targetWork.privacyState;
  if (targetWork.privacyState === "vault" /* Vault */ || secondaryWork.privacyState === "vault" /* Vault */) {
    resolvedPrivacy = "vault" /* Vault */;
  } else if (targetWork.privacyState === "private" /* Private */ || secondaryWork.privacyState === "private" /* Private */) {
    resolvedPrivacy = "private" /* Private */;
  }
  const resolvedCoverRef = params.preferredCoverRef !== void 0 ? params.preferredCoverRef : targetWork.coverRef ?? secondaryWork.coverRef;
  const mergedWork = {
    id: targetWork.id,
    title: targetWork.title,
    alternativeTitles: combinedAltTitles,
    aliases: combinedAliases,
    description: targetWork.description ?? secondaryWork.description,
    contentType: targetWork.contentType,
    origin: targetWork.origin !== "unknown" /* Unknown */ ? targetWork.origin : secondaryWork.origin,
    publicationState: targetWork.publicationState !== "unknown" /* Unknown */ ? targetWork.publicationState : secondaryWork.publicationState,
    privacyState: resolvedPrivacy,
    coverRef: resolvedCoverRef,
    externalIds: combinedExternalIds,
    genres: combinedGenres,
    tags: combinedTags,
    createdAt: targetWork.createdAt,
    updatedAt: now,
    deletedAt: null,
    // Target work remains active in vault
    mergedIntoWorkId: null
  };
  const targetRel = params.targetRelationship ?? null;
  const secondaryRel = params.secondaryRelationship ?? null;
  let resolvedRelationship = null;
  let archivedSecondaryRelationship = null;
  if (targetRel && secondaryRel) {
    let winner = "target";
    switch (progressPolicy) {
      case "keep_target":
        winner = "target";
        break;
      case "keep_secondary":
        winner = "secondary";
        break;
      case "highest_progress": {
        const anchorA = targetRel.currentAnchor;
        const anchorB = secondaryRel.currentAnchor;
        if (!anchorA && anchorB) {
          winner = "secondary";
        } else if (anchorA && !anchorB) {
          winner = "target";
        } else if (anchorA && anchorB) {
          const numA = extractNumericProgress(anchorA);
          const numB = extractNumericProgress(anchorB);
          if (numA !== null && numB !== null) {
            winner = numB > numA ? "secondary" : "target";
          } else {
            const tA = Date.parse(targetRel.lastActiveAt ?? targetRel.updatedAt) || 0;
            const tB = Date.parse(secondaryRel.lastActiveAt ?? secondaryRel.updatedAt) || 0;
            winner = tB > tA ? "secondary" : "target";
          }
        } else {
          winner = "target";
        }
        break;
      }
      case "most_recent": {
        const timeA = Date.parse(targetRel.lastActiveAt ?? targetRel.updatedAt) || 0;
        const timeB = Date.parse(secondaryRel.lastActiveAt ?? secondaryRel.updatedAt) || 0;
        winner = timeB > timeA ? "secondary" : "target";
        break;
      }
    }
    const winningRel = winner === "target" ? targetRel : secondaryRel;
    const losingRel = winner === "target" ? secondaryRel : targetRel;
    const validDates = [targetRel.startedAt, secondaryRel.startedAt].filter((d) => Boolean(d));
    const earliestStartedAt = validDates.length > 0 ? validDates.reduce((min, cur) => Date.parse(cur) < Date.parse(min) ? cur : min) : null;
    const activeDates = [targetRel.lastActiveAt, secondaryRel.lastActiveAt].filter((d) => Boolean(d));
    const latestActiveAt = activeDates.length > 0 ? activeDates.reduce((max, cur) => Date.parse(cur) > Date.parse(max) ? cur : max) : null;
    const completedDates = [targetRel.completedAt, secondaryRel.completedAt].filter((d) => Boolean(d));
    const latestCompletedAt = completedDates.length > 0 ? completedDates.reduce((max, cur) => Date.parse(cur) > Date.parse(max) ? cur : max) : null;
    const resolvedPriority = pickHigherPriority(targetRel.priority, secondaryRel.priority);
    let resolvedState = winningRel.personalState;
    if (resolvedState === "planned" /* Planned */ && losingRel.personalState !== "planned" /* Planned */) {
      resolvedState = losingRel.personalState;
    }
    resolvedRelationship = {
      ...targetRel,
      personalState: resolvedState,
      priority: resolvedPriority,
      startedAt: earliestStartedAt,
      lastActiveAt: latestActiveAt,
      completedAt: resolvedState === "completed" /* Completed */ ? latestCompletedAt ?? now : null,
      currentAnchor: winningRel.currentAnchor,
      preferredAccessPathId: targetRel.preferredAccessPathId ?? secondaryRel.preferredAccessPathId,
      updatedAt: now
    };
    archivedSecondaryRelationship = applyReadingRelationshipPatch(secondaryRel, {
      personalState: "archived" /* Archived */,
      stoppedAt: secondaryRel.stoppedAt ?? now,
      stoppedReason: "merged",
      stoppedNote: `Merged into work ${targetWork.id}`
    });
  } else if (targetRel) {
    resolvedRelationship = targetRel;
  } else if (secondaryRel) {
    resolvedRelationship = {
      ...secondaryRel,
      workId: targetWork.id,
      updatedAt: now
    };
  }
  const allAccessPaths = params.accessPaths ?? [];
  const targetHasPreferred = allAccessPaths.some(
    (p) => p.workId === targetWork.id && p.isPreferred
  );
  const rePointedAccessPaths = allAccessPaths.map((path) => {
    if (path.workId === secondaryWork.id) {
      return {
        ...path,
        workId: targetWork.id,
        // If target already had a preferred path, secondary's preferred flag is downgraded
        isPreferred: targetHasPreferred ? false : path.isPreferred,
        updatedAt: now
      };
    }
    return path;
  });
  const allMemories = params.memories ?? [];
  const rePointedMemories = allMemories.map((mem) => {
    if (mem.workId === secondaryWork.id) {
      return {
        ...mem,
        workId: targetWork.id,
        updatedAt: now
      };
    }
    return mem;
  });
  const allProgressEvents = params.progressHistory ?? [];
  const rePointedProgressEvents = allProgressEvents.map((event2) => {
    if (event2.workId === secondaryWork.id) {
      return {
        ...event2,
        workId: targetWork.id,
        readingRelationshipId: resolvedRelationship?.id ?? event2.readingRelationshipId
      };
    }
    return event2;
  });
  const allAttempts = params.readingAttempts ?? [];
  const rePointedReadingAttempts = allAttempts.map((attempt) => {
    if (attempt.workId === secondaryWork.id) {
      return {
        ...attempt,
        workId: targetWork.id,
        readingRelationshipId: resolvedRelationship?.id ?? attempt.readingRelationshipId
      };
    }
    return attempt;
  });
  const allSessions = params.readingSessions ?? [];
  const rePointedReadingSessions = allSessions.map((session) => {
    if (session.workId === secondaryWork.id) {
      return {
        ...session,
        workId: targetWork.id,
        readingRelationshipId: resolvedRelationship?.id ?? session.readingRelationshipId
      };
    }
    return session;
  });
  const archivedSecondaryWork = {
    ...secondaryWork,
    deletedAt: now,
    mergedIntoWorkId: targetWork.id,
    updatedAt: now
  };
  const archiveRecord = createArchiveRecord({
    id: newId14(),
    workId: secondaryWork.id,
    reason: "merged" /* Merged */,
    note: params.archiveNote ?? `Merged into "${targetWork.title}" (${targetWork.id})`,
    whyStopped: null,
    previousState: secondaryRel?.personalState ?? null,
    previousProgress: secondaryRel?.currentAnchor ?? null,
    archivedAt: now,
    mergedIntoWorkId: targetWork.id,
    metadata: {
      targetWorkId: targetWork.id,
      targetWorkTitle: targetWork.title,
      progressPolicy
    }
  });
  const event = createDomainEvent("work.merged", {
    sourceId: secondaryWork.id,
    targetId: targetWork.id,
    resolution: progressPolicy
  }, void 0, now);
  return {
    mergedWork,
    archivedSecondaryWork,
    archiveRecord,
    resolvedRelationship,
    archivedSecondaryRelationship,
    rePointedAccessPaths,
    rePointedMemories,
    rePointedProgressEvents,
    rePointedReadingAttempts,
    rePointedReadingSessions,
    event
  };
}

// src/use-cases/work-adaptation-graph.ts
function detectTemporalCycle(relations) {
  const temporalAdj = /* @__PURE__ */ new Map();
  const allNodes = /* @__PURE__ */ new Set();
  function addTemporalEdge(from, to) {
    allNodes.add(from);
    allNodes.add(to);
    let neighbors = temporalAdj.get(from);
    if (!neighbors) {
      neighbors = /* @__PURE__ */ new Set();
      temporalAdj.set(from, neighbors);
    }
    neighbors.add(to);
  }
  for (const relation of relations) {
    if (relation.relationType === "prequel" /* Prequel */) {
      addTemporalEdge(relation.sourceWorkId, relation.targetWorkId);
    } else if (relation.relationType === "sequel" /* Sequel */) {
      addTemporalEdge(relation.targetWorkId, relation.sourceWorkId);
    }
  }
  const visitState = /* @__PURE__ */ new Map();
  const parent = /* @__PURE__ */ new Map();
  for (const startNode of allNodes) {
    if ((visitState.get(startNode) ?? 0) !== 0) {
      continue;
    }
    const cyclePath = dfsCycleCheck(startNode, temporalAdj, visitState, parent);
    if (cyclePath) {
      return cyclePath;
    }
  }
  return null;
}
function dfsCycleCheck(current, adj, visitState, parent) {
  visitState.set(current, 1);
  const neighbors = adj.get(current) ?? /* @__PURE__ */ new Set();
  for (const neighbor of neighbors) {
    const state = visitState.get(neighbor) ?? 0;
    if (state === 1) {
      const path = [neighbor];
      let curr = current;
      while (curr !== neighbor) {
        path.unshift(curr);
        curr = parent.get(curr);
      }
      path.unshift(neighbor);
      return path;
    }
    if (state === 0) {
      parent.set(neighbor, current);
      const cycle = dfsCycleCheck(neighbor, adj, visitState, parent);
      if (cycle) {
        return cycle;
      }
    }
  }
  visitState.set(current, 2);
  return null;
}
function hasTemporalCycle(relations) {
  return detectTemporalCycle(relations) !== null;
}
function assertNoTemporalCycles(relations) {
  const cycle = detectTemporalCycle(relations);
  if (cycle) {
    throw new Error(
      `WorkRelation invariant violated: cycle detected in linear temporal relations: ${cycle.join(" -> ")}`
    );
  }
}
function validateWorkRelationGraph(relations) {
  const seenPairs = /* @__PURE__ */ new Set();
  for (const relation of relations) {
    validateWorkRelationInvariant(relation);
    const pairKey = [relation.sourceWorkId, relation.targetWorkId].sort().join("<->");
    if (seenPairs.has(pairKey)) {
      throw new Error(
        `WorkRelation invariant violated: duplicate relation between the same pair of works (${relation.sourceWorkId} and ${relation.targetWorkId})`
      );
    }
    seenPairs.add(pairKey);
  }
  assertNoTemporalCycles(relations);
}
function createPlaceholderWork(id) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  return {
    id,
    title: `Work (${id})`,
    alternativeTitles: [],
    aliases: [],
    description: null,
    contentType: "manga" /* Manga */,
    origin: "unknown" /* Unknown */,
    publicationState: "unknown" /* Unknown */,
    privacyState: "public" /* Public */,
    coverRef: null,
    externalIds: [],
    genres: [],
    tags: [],
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    mergedIntoWorkId: null
  };
}
function enrichWorkNode(targetWorkId, worksMap, edge) {
  const work = worksMap.get(targetWorkId) ?? createPlaceholderWork(targetWorkId);
  return {
    ...work,
    workId: targetWorkId,
    relationType: edge.relationType,
    relation: edge.relation,
    work,
    notes: edge.relation.notes ?? edge.relation.description ?? null
  };
}
function buildWorkRelationGraph(relations, works) {
  validateWorkRelationGraph(relations);
  const worksMap = /* @__PURE__ */ new Map();
  if (works) {
    for (const work of works) {
      worksMap.set(work.id, work);
    }
  }
  const adjacencyList = /* @__PURE__ */ new Map();
  function addEdge(sourceId, edge) {
    let edges = adjacencyList.get(sourceId);
    if (!edges) {
      edges = [];
      adjacencyList.set(sourceId, edges);
    }
    edges.push(edge);
  }
  for (const relation of relations) {
    addEdge(relation.sourceWorkId, {
      targetWorkId: relation.targetWorkId,
      relationType: relation.relationType,
      relation,
      isInverted: false
    });
    addEdge(relation.targetWorkId, {
      targetWorkId: relation.sourceWorkId,
      relationType: getInverseRelationType(relation.relationType),
      relation: invertWorkRelation(relation),
      isInverted: true
    });
  }
  return {
    relations: Object.freeze([...relations]),
    works: worksMap,
    adjacencyList,
    getRelatedWorks(workId, filterType) {
      const edges = adjacencyList.get(workId) ?? [];
      const filtered = filterType ? edges.filter((e) => e.relationType === filterType) : edges;
      return filtered.map((e) => enrichWorkNode(e.targetWorkId, worksMap, e));
    },
    getAdaptations(workId, options) {
      const transitive = options?.transitive ?? false;
      const results = [];
      const visited = /* @__PURE__ */ new Set();
      const queue = [workId];
      visited.add(workId);
      while (queue.length > 0) {
        const currentId = queue.shift();
        const edges = adjacencyList.get(currentId) ?? [];
        const adaptationEdges = edges.filter(
          (e) => e.relationType === "original_work_of" /* OriginalWorkOf */
        );
        for (const edge of adaptationEdges) {
          if (!visited.has(edge.targetWorkId)) {
            visited.add(edge.targetWorkId);
            results.push(enrichWorkNode(edge.targetWorkId, worksMap, edge));
            if (transitive) {
              queue.push(edge.targetWorkId);
            }
          }
        }
      }
      return results;
    },
    getOriginalWork(workId) {
      const edges = adjacencyList.get(workId) ?? [];
      const originalEdge = edges.find((e) => e.relationType === "adaptation_of" /* AdaptationOf */);
      if (!originalEdge) {
        return null;
      }
      return enrichWorkNode(originalEdge.targetWorkId, worksMap, originalEdge);
    },
    getChronologicalSequence(workId) {
      const componentNodes = /* @__PURE__ */ new Set();
      const queue = [workId];
      componentNodes.add(workId);
      while (queue.length > 0) {
        const curr = queue.shift();
        const edges = adjacencyList.get(curr) ?? [];
        for (const edge of edges) {
          if (edge.relationType === "sequel" /* Sequel */ || edge.relationType === "prequel" /* Prequel */) {
            if (!componentNodes.has(edge.targetWorkId)) {
              componentNodes.add(edge.targetWorkId);
              queue.push(edge.targetWorkId);
            }
          }
        }
      }
      if (componentNodes.size === 1) {
        const singleWork = worksMap.get(workId) ?? createPlaceholderWork(workId);
        return [
          {
            ...singleWork,
            workId,
            work: singleWork,
            sequenceIndex: 0
          }
        ];
      }
      const forwardAdj = /* @__PURE__ */ new Map();
      const inDegree = /* @__PURE__ */ new Map();
      for (const node of componentNodes) {
        forwardAdj.set(node, /* @__PURE__ */ new Set());
        inDegree.set(node, 0);
      }
      for (const u of componentNodes) {
        const edges = adjacencyList.get(u) ?? [];
        for (const edge of edges) {
          if (componentNodes.has(edge.targetWorkId)) {
            if (edge.relationType === "prequel" /* Prequel */) {
              const targets = forwardAdj.get(u);
              if (!targets.has(edge.targetWorkId)) {
                targets.add(edge.targetWorkId);
                inDegree.set(edge.targetWorkId, (inDegree.get(edge.targetWorkId) ?? 0) + 1);
              }
            }
          }
        }
      }
      const readyQueue = [];
      for (const node of componentNodes) {
        if ((inDegree.get(node) ?? 0) === 0) {
          readyQueue.push(node);
        }
      }
      readyQueue.sort((a, b) => a.localeCompare(b));
      const orderedSequence = [];
      while (readyQueue.length > 0) {
        const current = readyQueue.shift();
        orderedSequence.push(current);
        const successors = forwardAdj.get(current) ?? /* @__PURE__ */ new Set();
        const nextReady = [];
        for (const successor of successors) {
          const newDeg = (inDegree.get(successor) ?? 1) - 1;
          inDegree.set(successor, newDeg);
          if (newDeg === 0) {
            nextReady.push(successor);
          }
        }
        nextReady.sort((a, b) => a.localeCompare(b));
        readyQueue.push(...nextReady);
      }
      if (orderedSequence.length < componentNodes.size) {
        for (const node of componentNodes) {
          if (!orderedSequence.includes(node)) {
            orderedSequence.push(node);
          }
        }
      }
      return orderedSequence.map((id, index) => {
        const work = worksMap.get(id) ?? createPlaceholderWork(id);
        return {
          ...work,
          workId: id,
          work,
          sequenceIndex: index
        };
      });
    },
    getDirectRelations(workId) {
      return this.getRelatedWorks(workId);
    },
    hasRelations(workId) {
      return (adjacencyList.get(workId)?.length ?? 0) > 0;
    }
  };
}

// src/deduplication.ts
var DuplicateConfidence = /* @__PURE__ */ ((DuplicateConfidence2) => {
  DuplicateConfidence2["Exact"] = "exact";
  DuplicateConfidence2["High"] = "high";
  DuplicateConfidence2["Probable"] = "probable";
  DuplicateConfidence2["Low"] = "low";
  DuplicateConfidence2["None"] = "none";
  return DuplicateConfidence2;
})(DuplicateConfidence || {});
function cleanTitle2(raw) {
  let cleaned = raw.toLowerCase().replace(/[^a-z0-9\s]/gi, " ").replace(/\s+/g, " ").trim();
  return cleaned;
}
function levenshteinDistance2(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = i;
    for (let j = 1; j <= b.length; j++) {
      const val = a[i - 1] === b[j - 1] ? row[j - 1] : Math.min(row[j - 1], row[j], prev) + 1;
      row[j - 1] = prev;
      prev = val;
    }
    row[b.length] = prev;
  }
  return row[b.length];
}
function levenshteinSimilarity2(a, b) {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshteinDistance2(a, b);
  return Math.max(0, 1 - dist / maxLen);
}
function tokenJaccardSimilarity2(a, b) {
  const tokensA = new Set(a.split(/\s+/).filter((t) => t.length > 1));
  const tokensB = new Set(b.split(/\s+/).filter((t) => t.length > 1));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const token of tokensA) {
    if (tokensB.has(token)) {
      intersection++;
    }
  }
  const union = tokensA.size + tokensB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}
function calculateUrlSimilarity(url1, url2) {
  const norm1 = normalizeUrl(url1);
  const norm2 = normalizeUrl(url2);
  if (!norm1.isValid || !norm2.isValid) {
    return 0;
  }
  if (norm1.normalizedUrl === norm2.normalizedUrl) {
    return 1;
  }
  if (norm1.domain !== norm2.domain) {
    return 0;
  }
  const slug1 = cleanTitle2(norm1.cleanSlug);
  const slug2 = cleanTitle2(norm2.cleanSlug);
  if (slug1 === slug2) {
    return 0.95;
  }
  const slugSim = levenshteinSimilarity2(slug1, slug2);
  if (slugSim >= 0.8) {
    return slugSim * 0.9;
  }
  const path1 = norm1.path.split("/").filter((p) => p);
  const path2 = norm2.path.split("/").filter((p) => p);
  if (path1.length === 0 && path2.length === 0) {
    return 0.7;
  }
  if (path1.length > 0 && path2.length > 0 && path1[0] === path2[0]) {
    return 0.6;
  }
  return 0;
}
function calculateTitleSimilarity(title1, title2) {
  const cleaned1 = cleanTitle2(title1);
  const cleaned2 = cleanTitle2(title2);
  if (!cleaned1 || !cleaned2) {
    return 0;
  }
  if (cleaned1 === cleaned2) {
    return 1;
  }
  if (cleaned1.length >= 4 && cleaned2.includes(cleaned1) || cleaned2.length >= 4 && cleaned1.includes(cleaned2)) {
    return 0.85;
  }
  const levSim = levenshteinSimilarity2(cleaned1, cleaned2);
  if (levSim >= 0.75) {
    return levSim;
  }
  const jaccardSim = tokenJaccardSimilarity2(cleaned1, cleaned2);
  if (jaccardSim >= 0.6) {
    return jaccardSim;
  }
  return 0;
}
function providerSpecificMatch(norm1, norm2) {
  if (norm1.domain === "mangadex.org" && norm2.domain === "mangadex.org") {
    const id1 = norm1.path.split("/")[1];
    const id2 = norm2.path.split("/")[1];
    if (id1 && id2 && id1 === id2) {
      return 1;
    }
  }
  if (norm1.domain === "webtoons.com" && norm2.domain === "webtoons.com") {
    const parts1 = norm1.path.split("/");
    const parts2 = norm2.path.split("/");
    const slug1 = parts1[parts1.length - 1];
    const slug2 = parts2[parts2.length - 1];
    if (slug1 && slug2 && slug1 === slug2) {
      return 0.95;
    }
  }
  return null;
}
function detectDuplicate(capture, existingWorks) {
  const captureUrl = capture.url?.trim() ?? "";
  const captureTitle = capture.title?.trim() ?? "";
  if (!captureUrl && !captureTitle) {
    return {
      captureId: capture.id,
      bestMatch: null,
      candidates: [],
      isDuplicate: false
    };
  }
  const normCaptureUrl = captureUrl ? normalizeUrl(captureUrl) : null;
  const cleanedCaptureTitle = captureTitle ? cleanTitle2(captureTitle) : "";
  const captureSlug = normCaptureUrl?.slug ? cleanTitle2(normCaptureUrl.slug) : "";
  const candidates = [];
  for (const work of existingWorks) {
    const reasons = [];
    let bestScore = 0;
    if (normCaptureUrl && normCaptureUrl.isValid) {
      for (const extId of work.externalIds) {
        if (extId.url) {
          const urlSim = calculateUrlSimilarity(captureUrl, extId.url);
          if (urlSim > bestScore) {
            bestScore = urlSim;
            if (urlSim >= 0.95) {
              reasons.push(`URL match with ${extId.provider} reference`);
            } else if (urlSim >= 0.8) {
              reasons.push(`Strong URL similarity with ${extId.provider} reference`);
            }
          }
        }
      }
      if (normCaptureUrl.isValid) {
        for (const extId of work.externalIds) {
          if (extId.url) {
            const normExtUrl = normalizeUrl(extId.url);
            if (normExtUrl.isValid) {
              const providerMatch = providerSpecificMatch(normCaptureUrl, normExtUrl);
              if (providerMatch !== null && providerMatch > bestScore) {
                bestScore = providerMatch;
                reasons.push(`Provider-specific match with ${extId.provider}`);
              }
            }
          }
        }
      }
    }
    const titlesToCheck = [
      { title: work.title, type: "primary" },
      ...work.alternativeTitles.map((t) => ({ title: t, type: "alternative" })),
      ...work.aliases.map((a) => ({ title: a, type: "alias" }))
    ];
    for (const item of titlesToCheck) {
      const cleanedTarget = cleanTitle2(item.title);
      if (!cleanedTarget) continue;
      const titleSim = calculateTitleSimilarity(captureTitle, item.title);
      if (titleSim > bestScore) {
        bestScore = titleSim;
        if (titleSim >= 0.95) {
          reasons.push(`Exact title match with ${item.type} title "${item.title}"`);
        } else if (titleSim >= 0.8) {
          reasons.push(`High title similarity with ${item.type} title "${item.title}"`);
        } else if (titleSim >= 0.6) {
          reasons.push(`Moderate title similarity with ${item.type} title "${item.title}"`);
        }
      }
      if (captureSlug) {
        const slugTitleSim = calculateTitleSimilarity(captureSlug, item.title);
        if (slugTitleSim >= 0.8 && slugTitleSim * 0.9 > bestScore) {
          bestScore = slugTitleSim * 0.9;
          reasons.push(`URL slug matches ${item.type} title "${item.title}"`);
        }
      }
    }
    if (normCaptureUrl && normCaptureUrl.isValid) {
      for (const extId of work.externalIds) {
        if (normCaptureUrl.path.includes(extId.externalId) || normCaptureUrl.path.includes(extId.externalId.replace(/-/g, "_"))) {
          const idMatchScore = 0.9;
          if (idMatchScore > bestScore) {
            bestScore = idMatchScore;
            reasons.push(`URL contains ${extId.provider} ID "${extId.externalId}"`);
          }
        }
      }
    }
    if (bestScore >= 0.3) {
      let confidence;
      if (bestScore >= 0.95) confidence = "exact" /* Exact */;
      else if (bestScore >= 0.8) confidence = "high" /* High */;
      else if (bestScore >= 0.5) confidence = "probable" /* Probable */;
      else confidence = "low" /* Low */;
      candidates.push({
        workId: work.id,
        workTitle: work.title,
        score: Math.round(bestScore * 100) / 100,
        confidence,
        reasons
      });
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  const bestMatch = candidates[0] ?? null;
  const isDuplicate = bestMatch !== null && bestMatch.confidence !== "low" /* Low */ && bestMatch.confidence !== "none" /* None */;
  return {
    captureId: capture.id,
    bestMatch,
    candidates,
    isDuplicate
  };
}
function calculateWorkSimilarity(work1, work2) {
  for (const extId1 of work1.externalIds) {
    for (const extId2 of work2.externalIds) {
      if (extId1.provider === extId2.provider && extId1.externalId === extId2.externalId) {
        return 1;
      }
    }
  }
  const titleSim = calculateTitleSimilarity(work1.title, work2.title);
  if (titleSim >= 0.95) {
    return titleSim;
  }
  let bestAltSim = 0;
  for (const alt1 of [...work1.alternativeTitles, ...work1.aliases]) {
    for (const alt2 of [...work2.alternativeTitles, ...work2.aliases]) {
      const sim = calculateTitleSimilarity(alt1, alt2);
      if (sim > bestAltSim) {
        bestAltSim = sim;
      }
    }
  }
  return Math.max(titleSim, bestAltSim);
}
export {
  AccessCapability,
  AccessPathKind,
  ArchiveReason,
  CaptureSource,
  CaptureState,
  ContentOrigin,
  ContentType,
  DEFAULT_RETURN_ENGINE_CONFIG,
  DuplicateConfidence,
  HealthScheduler,
  INVERSE_RELATION_MAP,
  LoreEntityType,
  MemoryAssetType,
  MemoryPrivacyState,
  MemoryType,
  PersonalState,
  PrivacyState,
  ProgressConfidence,
  ProgressMethod,
  PublicationState,
  ReadingPriority,
  RelationType,
  ReturnSignalType,
  SourceCapabilityStatus,
  SourceHealth,
  SourceHealthState,
  SpoilerLevel,
  TriageConfidence,
  TriageRecommendation,
  UpdateCertainty,
  WorkRelationType,
  applyAccessPathPatch,
  applyCapturePatch,
  applyHealthCheckResult,
  applyLoreEntryPatch,
  applyMemoryPatch,
  applyPublicationPatch,
  applyReadingRelationshipPatch,
  applyUpdateCertaintyValidity,
  applyUserListPatch,
  applyWorkPatch,
  archiveWork,
  assertNoTemporalCycles,
  buildWorkRelationGraph,
  calculateTitleSimilarity,
  calculateUrlSimilarity,
  calculateWorkSimilarity,
  carryForwardConditionalValidators,
  compareReturnSignals,
  compareWorksForDuplicates,
  computeReturnSignals,
  createArchiveRecord,
  createCapture,
  createCaptureBatch,
  createConditionalRequestHeaders,
  createDomainEvent,
  createExternalIdentifier,
  createFeedAccessPath,
  createHealthMonitorState,
  createListItem,
  createLocalFileAccessPath,
  createLoreEntry,
  createManualAccessPath,
  createMemory,
  createMemoryAsset,
  createProgressEvent,
  createPublication,
  createPublicationItem,
  createReadingAttempt,
  createReadingRelationship,
  createReadingSession,
  createReturnSignal,
  createUpdateEntry,
  createUserList,
  createWebAccessPath,
  createWork,
  createWorkRelation,
  detectDuplicate,
  detectTemporalCycle,
  displayProgress,
  evaluateBatchReleases,
  evaluateNewRelease,
  extractNumericProgress,
  findDuplicateCandidates,
  formatProgressAnchor,
  getInverseRelationType,
  hasTemporalCycle,
  invertWorkRelation,
  isEligibleForHealthCheck,
  mergeWorksNonDestructive,
  normalizeAccessPathHealth,
  normalizePrivacyState,
  normalizeProgressAnchor,
  normalizeTitleForDeduplication,
  normalizeUrl,
  recordHealthCheckFailure,
  recordHealthCheckSuccess,
  selectFallbackAccessPath,
  stripSubtitles,
  toAccessPathSummary,
  toWorkSummary,
  triageCaptureItem,
  validateAccessPathInvariant,
  validateArchiveRecordInvariant,
  validateCaptureInvariant,
  validateListItemInvariant,
  validateLoreEntryInvariant,
  validateMemoryInvariant,
  validateProgressAnchor,
  validatePublicationInvariant,
  validateReadingRelationshipInvariant,
  validateUserListInvariant,
  validateWorkInvariant,
  validateWorkRelationGraph,
  validateWorkRelationInvariant
};
